// i18n — English strings (source of truth).
//
// Convention: <surface>.<element>.<state>
// Layer 7 (docs/ui/07-voice.md) is the spec authority for every string.
// This file IS the runtime source of truth — if a string here differs
// from the spec, this file wins and the spec must be updated.
//
// DO NOT add a key here without a parallel key in es.ts. The TypeScript
// type system enforces key parity at compile time (see index.ts).

const en = {
  // ── Product ────────────────────────────────────────────────────────
  product: {
    name: 'Venuz',
    tagline: 'AI coding IDE for founders',
  },

  // ── Status verbs (Layer 7 D70) ─────────────────────────────────────
  status: {
    running: 'Working',
    waiting: 'Waiting',
    finished: 'Done',
    error: 'Failed',
    interrupted: 'Stopped',
    stuck: 'Stuck',
    archived: 'Archived',
    // Atom-level idle aria-label for `StatusIcon`. After sprint
    // `sidebar-status-icons-binary` (2026-05-12) this is the only
    // idle copy used anywhere — the sidebar-level `agentList.idle`
    // badge ('Idle . Send a message') was removed when the
    // CircleCheck icon took over the "ready for input" affordance.
    idle: 'Idle',
  },

  // ── Topbar ─────────────────────────────────────────────────────────
  topbar: {
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    signOut: 'Sign out',
    notifications: 'Notifications',
    noNotifications: 'No notifications',
    // Sprint UI-8 (A5) — surfaced inside the Bell popover when the
    // founder has at least one unread agent. Plural-aware strings live
    // in the i18n layer; English collapses to a single template that
    // formats fine for 1 or N.
    unreadAgents: '{count} unread agents',
    settings: 'Settings',
    accountMenu: 'Account menu',
    theme: 'Theme',
    // Sprint S20.5 PR-3 (D013-EC-69) — persistent runner-status banner.
    // The i18n layer doesn't ship ICU; we surface explicit one/many
    // keys so the founder reads natural plural copy in both locales.
    runnerBanner: {
      unavailableOne: '1 server offline. New agents will route to the rest.',
      unavailableMany: '{count} servers offline. New agents will route to the rest.',
      allDown: 'All servers offline. New agents can\u2019t be created right now.',
      goToServers: 'Open Servers',
    },
  },

  // ── Connection status ──────────────────────────────────────────────
  connection: {
    connected: 'Connected',
    reconnecting: 'Reconnecting',
    disconnected: 'Disconnected — tap to retry',
  },

  // Sprint runner-picker-on-create — modal that opens on "+" so the
  // founder can choose which runner the new agent lands on, with
  // remaining-capacity visible at choice time.
  runnerPicker: {
    title: 'Choose a runner',
    subtitle: 'New agents land on the runner you pick.',
    autoLabel: 'Auto (recommended)',
    autoHelper: 'Let the office pick the runner with the most capacity left.',
    capacityFree: '{percent}% free',
    capacityUnknown: 'Capacity unavailable',
    // Sprint runner-picker-session-primary — subtitle line under the
    // session-5h "% free" headline. Built by joining the bucket parts
    // with " · ". Same unit ("free") as the primary so the founder
    // does not need to mentally switch between "free" and "used".
    bucketWeekly: 'Weekly {percent}% free',
    bucketSonnet: 'Sonnet {percent}% free',
    // Sprint usage-limits-v2 (2026-07-08) — subtitle segment when the
    // canonical `limits[]` array surfaces a model-scoped weekly limit.
    // Legacy sonnet bucket keeps its own key; new scoped kinds render
    // "Weekly · <model>" with per-model free %.
    bucketWeeklyScoped: 'Weekly · scoped {percent}% free',
    bucketWeeklyScopedModel: 'Weekly · {model} {percent}% free',
    offlineReason: 'Offline — pick another runner.',
    drainingReason: 'Draining — accepting no new agents.',
    confirm: 'Create agent',
    cancel: 'Cancel',
  },

  // ── Sidebar ────────────────────────────────────────────────────────
  sidebar: {
    newAgent: 'New Agent',
    newAgentDisabled: 'New agents paused — server low on memory',
    search: 'Search agents',
    // Sprint UI-7b — accessible label for the kbd hint rendered inside
    // SearchBar when the field is empty. macOS shows "⌘K", other OSes
    // show "Ctrl+K"; the label covers both for screen readers.
    searchShortcutAriaLabel: 'Focus search (Cmd+K)',
    // Sprint UI-8 (A1) — aria-label for the 4 px drag handle that resizes the
    // sidebar. Pre-existing string, lifted into i18n now that the surrounding
    // file passes through lint after the Topbar removal.
    resizeAriaLabel: 'Resize sidebar',
    empty: {
      title: 'No agents yet',
      subtitle: 'Start your first one',
    },
    showMore: 'Show {count} more',
    // Sprint agent-stages — the recency sections (PINNED/TODAY/YESTERDAY/
    // OLDER) were retired; sections are now the founder's workflow stages
    // (`stages` group below). ARCHIVED stays.
    section: {
      archived: 'ARCHIVED',
    },
    project: {
      label: 'Project',
      all: 'All projects',
    },
  },

  // ── Workflow stages (sprint agent-stages) ───────────────────────────
  stages: {
    noStage: 'No stage',
    moveTo: 'Move to stage',
    newStage: 'New stage…',
    manage: 'Manage stages…',
    manageTitle: 'Stages',
    createTitle: 'New stage',
    editTitle: 'Edit stage',
    newStageButton: 'New stage',
    emptyList: 'No stages yet. Create the first one.',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. Soak',
    colorLabel: 'Color',
    colorOption: 'Color {n}',
    create: 'Create',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    rename: 'Edit',
    delete: 'Delete',
    moveUp: 'Move up',
    moveDown: 'Move down',
    filterAll: 'All',
    deleteConfirmTitle: 'Delete stage?',
    deleteConfirmBody:
      'The {count} agents in "{name}" will move to "No stage". No agents are deleted.',
    createError: 'Could not create the stage',
    updateError: 'Could not update the stage',
    deleteError: 'Could not delete the stage',
  },

  // ── Workspace column ────────────────────────────────────────────────
  workspace: {
    addProject: 'New project',
    newProject: 'New project',
    repoUrl: 'Repo URL',
    slugInvalid: 'Slug must start with a letter or number and contain only lowercase letters, numbers, dots, hyphens.',
    cloning: 'Cloning…',
    creating: 'Creating…',
    create: 'Create',
    done: 'Done',
    namePlaceholder: 'My Project',
    slugPlaceholder: 'my-project',
    repoPlaceholder: 'git@github.com:org/repo.git',
    branchPlaceholder: 'main',
  },

  // ── Workspace customization (Sprint 11b.2) ─────────────────────────
  workspaceCustomization: {
    edit: 'Edit project…',
    archive: 'Archive',
    editTitle: 'Edit project',
    initials: 'Initials',
    initialsHint: 'Exactly 2 letters or numbers, uppercase.',
    color: 'Color',
    autoColor: 'Auto color',
    autoGlyph: 'A',
    colorOption: 'Color {n}',
    resetAuto: 'Reset to auto',
    save: 'Save',
    saving: 'Saving…',
    reorderError: 'Couldn’t reorder — try again.',
    updateError: 'Couldn’t update — try again.',
    syncIncomplete: 'Sync incomplete on {count} server(s).',
  },

  // ── Agent context menu ─────────────────────────────────────────────
  contextMenu: {
    markRead: 'Mark read',
    markUnread: 'Mark unread',
    rename: 'Rename',
    fork: 'Fork',
    archive: 'Archive',
    restore: 'Restore',
    delete: 'Delete',
    ariaLabel: 'Agent actions',
  },

  // ── Chat header ────────────────────────────────────────────────────
  chatHeader: {
    renameTooltipDesktop: 'Double-click to rename',
    renameTooltipMobile: 'Tap to rename',
    moreActions: 'More actions',
    backAriaLabel: 'Back to agent list',
  },

  // ── Chat — empty state ─────────────────────────────────────────────
  chat: {
    empty: {
      title: 'Pick an agent or start a new one',
      subtitle: 'Select an agent from the sidebar to view its messages, send a follow-up, or stop the run.',
      openSidebar: 'Show agent list',
    },
    // Sprint S20.5 PR-4 (D013-EC-70) — chat-header pill for the
    // `waiting` status. Mirrors the sidebar badge but with a slightly
    // longer hint because the chat header has more horizontal room and
    // is the founder's primary surface for the selected agent.
    idleHint: 'Idle · Send a message to continue.',
  },

  // ── Composer ────────────────────────────────────────────────────────
  composer: {
    heading: 'New agent',
    openSidebar: 'Open sidebar',
    placeholder: {
      new: 'What should this agent do?',
      continue: 'Send a message to continue this agent...',
      waiting: 'Send a message · / for commands · @ for mentions',
      running: 'Add a follow-up…',
      stuck: 'Send a message — the agent will pick it up.',
      finished: 'Send a message to continue this agent…',
      interrupted: 'Send a message to continue this agent…',
      errorRecoverable: 'Send a message to continue (will retry)…',
      errorUnrecoverable: 'Fork to start a new conversation',
      idle: 'Send a message…',
      working: 'Add a follow-up…',
      disconnected: 'Will send when reconnected',
      archived: 'This agent is archived. Restore it to continue.',
      permissionPending: 'Waiting for your permission decision above',
      default: 'Send a message…',
      exampleHint: 'Read the README and summarise it in 5 bullets...  ·  type / for commands, @ for mentions',
    },
    hint: {
      running: 'Waiting for the agent to finish the current turn…',
      stuck: 'No activity for several minutes — the process is alive but may be wedged.',
      finished: 'Agent finished — send to continue or fork to branch.',
      interrupted: 'Agent was stopped — send to resume or fork to branch.',
      error: 'Agent failed — send to retry, or fork from the last working turn.',
    },
    send: 'Send',
    sending: 'Sending…',
    starting: 'Starting…',
    stop: 'Stop',
    stopping: 'Stopping…',
    stopAriaLabel: 'Stop agent',
    sendAriaLabel: 'Send message',
    attachAriaLabel: 'Attach file',
    promptLabel: 'Prompt',
    promptTooLong: 'Prompt too long — remove {count} chars to send.',
    promptApproachingLimit: 'Approaching the prompt limit.',
    enterToSend: 'Enter to send · Shift + Enter for new line',
    memoryWarning: 'Server memory is low — new agents are paused until the running ones free up RAM.',
    couldNotStart: 'Could not start agent.',
    couldNotChangeAgent: 'Could not change agent',
    sendFailed: 'Message not sent. Tap to retry.',
    attachmentLost: 'An attachment was lost. Re-attach it and send again.',
    attachmentsLost: 'Some attachments were lost. Re-attach them and send again.',
    // Sprint attachments-anthropic-parity Phase D — accessible labels for
    // the image thumbnail + fullscreen lightbox (D7, D8, D9).
    imageLightboxOpen: 'View full image',
    imageLightboxClose: 'Close image',
    removeAttachmentAriaLabel: 'Remove attachment',
    retryAttachmentAriaLabel: 'Retry attachment',
    archived: {
      message: 'This agent is archived and no longer receives messages. To continue with it, unarchive it from the list.',
    },
    unrecoverable: {
      message: 'Agent ended with an error and cannot be resumed. Fork it to start a new conversation from this point.',
      forkCta: 'Fork conversation',
    },
    confirmModeSwitch: {
      toPlan: 'Switch to Plan mode?',
      toAgent: 'Switch to Agent mode?',
      body: 'An action is waiting for your approval. Switching modes will cancel it.',
      confirmPlan: 'Switch to Plan',
      confirmAgent: 'Switch to Agent',
    },
  },

  // ── Confirm dialogs ────────────────────────────────────────────────
  confirm: {
    cancel: 'Cancel',
    confirm: 'Confirm',
    working: 'Working…',
    stopAgent: {
      title: 'Stop this agent?',
      body: 'The current Claude process will be killed. The chat history stays in the archive.',
      confirm: 'Stop',
    },
    forkAgent: {
      title: 'Fork this agent?',
      body: 'A new agent resumes the same Claude session. Both agents share history up to this point and diverge afterward.',
      confirm: 'Fork',
    },
    deleteAgent: {
      title: 'Delete this agent?',
      body: 'The process is killed and the agent disappears from the active list. Cannot be undone.',
      confirm: 'Delete',
    },
    deleteAgentSidebar: {
      title: 'Delete agent?',
      bodyTemplate: '"{name}" will be stopped and removed. You have 5 seconds to undo.',
      confirm: 'Delete',
    },
    signOut: {
      title: 'Sign out?',
      body: "You'll need to sign in again to access the dashboard.",
      confirm: 'Sign out',
    },
    resetSettings: {
      title: 'Reset all settings?',
      body: 'This restores defaults. Cannot be undone.',
      confirm: 'Reset',
    },
    changeMode: {
      title: 'Switch to {mode} mode?',
      body: 'The agent will respawn under the new mode. Chat history stays; the running Claude process restarts.',
      confirm: 'Switch',
    },
  },

  // ── Toasts ─────────────────────────────────────────────────────────
  toast: {
    undo: 'Undo',
    dismiss: 'Dismiss notification',
    agentArchived: 'Agent archived',
    agentRestored: 'Agent restored',
    agentDeleted: 'Agent deleted',
    agentStopped: 'Agent stopped',
    undoFailed: 'Undo failed — agent will be purged in a few seconds',
    ariaLabel: 'Notifications',
    couldNotPin: 'Could not pin agent. Try again.',
    couldNotArchive: 'Could not archive agent. Try again.',
    couldNotRestore: 'Could not restore agent. Try again.',
    couldNotDelete: 'Could not delete agent. Try again.',
    couldNotRename: 'Could not rename agent. Try again.',
    couldNotSave: 'Could not save. Try again.',
    retry: 'Retry',
    // Sprint 20.3 PR-10 (D013-EC-43) — scheduler refused the spawn
    // because every runner is offline / drained / capped. Action label
    // for the spawn-error toast that points at the centralized usage
    // modal. Founder copy for the toast itself moved to
    // `errors.spawn.NO_RUNNER_AVAILABLE` in S20.5 PR-3 so every spawn
    // branch is owned by the same namespace.
    viewRunnerUsage: 'View runner usage',
  },

  // ── Login page ─────────────────────────────────────────────────────
  // ── Personal accounts (docs/cuentas-personales.md) ───────────────
  cuentas: {
    titulo: 'Accounts and sessions',
    volver: 'Office',
    hasEntradoComo: 'Signed in as {nombre}',
    salir: 'Sign out',
    cargando: 'Loading…',
    compartidaViva: 'The old shared password still works. It stops working as soon as Pau finishes his sign-up.',
    quienEstaDentro: 'Who is signed in',
    tusSesiones: 'Your sessions',
    estaSesion: 'this one',
    aparatoDesconocido: 'unknown device',
    lineaSesion: '{navegador} · IP {ip} · since {desde} · seen {visto}',
    cerrarEsta: 'Close this one',
    echar: 'Kick out',
    sinSesiones: 'No open sessions.',
    programas: 'Office programs',
    programasLinea: '{n} sessions of automatic programs running on the server itself. They only work from inside the server and expire in a day.',
    cuentasTitulo: 'Accounts',
    titular: 'Owner',
    socio: 'Partner',
    desactivada: 'deactivated',
    activa: 'active, with Google Authenticator',
    altaPendiente: 'sign-up pending',
    enlaceNuevo: 'New sign-up link',
    confirmarEnlaceNuevo: 'Give {nombre} a new sign-up link? Their password, Authenticator and codes stop working and their sessions are closed.',
    cerrarSusSesiones: 'Close their sessions',
    desactivar: 'Deactivate',
    confirmarDesactivar: 'Deactivate {nombre}? They are signed out now and cannot come back until you give them a new sign-up link.',
    enlaceAviso: 'Sign-up link for {nombre} (single use, until {hasta}). Send it privately:',
    copiar: 'Copy',
    usuario: 'Username',
    nombre: 'Name',
    ejemploUsuario: 'carlos',
    ejemploNombre: 'Carlos',
    darDeAlta: 'Add a partner',
    faltanDatos: 'Enter the username and the name.',
    registroTitulo: 'Who did what',
    cuando: 'When',
    quien: 'Who',
    que: 'What',
    desde: 'From',
    errorCarga: 'Could not load.',
    errorAccion: 'Could not do it.',
    ev: {
      entrada: 'Signed in',
      entradaAparatoNuevo: 'Signed in from a new device',
      entradaCompartida: 'Signed in with the shared password',
      entradaTrasAlta: 'Signed in after sign-up',
      entradaFallida: 'Failed sign-in ({detalle})',
      salida: 'Signed out',
      altaCompletada: 'Finished sign-up',
      sesionCerrada: 'Closed a session',
      sesionesCerradas: 'Closed every session of {detalle}',
      cuentaDesactivada: 'Deactivated the account of {detalle}',
      recuperacionUsada: 'Used a recovery code ({detalle})',
      compartidaApagada: 'Turned off the shared password',
      mensaje: 'Wrote to an agent: «{detalle}»',
      permiso: 'Answered a permission',
      despliegue: 'Decided a production release',
      aprobarPlan: 'Approved a plan',
      cambiosPlan: 'Asked for plan changes',
      pregunta: 'Answered an agent question',
      escribio: 'Wrote to an agent',
      contrato: 'Hired an agent',
      cerrarAgente: 'Asked to close an agent',
      seguir: 'Asked an agent to continue',
      permisoHerramienta: 'Answered a tool permission',
      gestionCuentas: 'Managed accounts',
      otra: 'Other action',
      noSeHizo: ' (not done)',
    },
    alta: {
      tituloEnlace: 'Sign-up link',
      enlaceIncompleto: 'This link is incomplete. Ask Pau for a new one.',
      errorEnlace: 'Could not open the link.',
      abriendo: 'Opening…',
      listo: 'Done, {nombre}',
      codigosExplica: 'These are your recovery codes. If you lose your phone, each one lets you in once instead of the app code.',
      soloAhora: 'You only see them now: keep them off your phone (password manager or printed).',
      copiar: 'Copy',
      descargar: 'Download',
      guardados: 'I have stored them somewhere safe',
      entrar: 'Enter the office',
      hola: 'Hi, {nombre}',
      explica: 'You are about to create your office account. Your username is «{usuario}».',
      paso1: '1. Scan this code with Google Authenticator',
      paso1Ayuda: 'In the app: «+» → «Scan a QR code».',
      qrAlt: 'QR code for Google Authenticator',
      verClave: 'On your phone and cannot scan? Type the key by hand',
      ocultarClave: 'Hide the key',
      paso2: '2. Choose your password',
      contrasena: 'Password',
      contrasenaAyuda: 'At least {min} characters. A phrase you do not use anywhere else is best.',
      repite: 'Repeat the password',
      paso3: '3. Type the code the app shows now',
      codigoAria: 'Google Authenticator code',
      crear: 'Create my account',
      errMin: 'The password needs at least {min} characters.',
      errNoCoinciden: 'The two passwords do not match.',
      errCodigo: 'Type the 6 digits the app shows now.',
      errGenerico: 'Could not finish sign-up. Try again.',
      fichero: 'Office recovery codes ({nombre}). Each one works only once.',
    },
  },

  login: {
    userLabel: 'Username',
    userEmpty: 'Enter your username.',
    codeLabel: 'Google Authenticator code',
    codeHelp: 'The 6 digits shown now in the app. Lost your phone? Enter one of your recovery codes.',
    codeEmpty: 'Enter the code.',
    withAccount: 'Sign in with my account',
    passwordLabel: 'Password',
    passwordEmpty: 'Enter your password.',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    couldNotSignIn: 'Could not sign in. Try again.',
    wrongPassword: 'Wrong password',
    tooManyAttempts: 'Too many attempts — try again in {seconds}s',
  },

  // ── Notification feed (Sprint notification-center) ─────────────────
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    openAction: 'View',
    // 04-11 — the toast of a notice with an address (Santi's): who and what they say.
    conTexto: '{titulo}: {texto}',
  },

  // ── Settings page ──────────────────────────────────────────────────
  settings: {
    heading: 'Settings',
    backToChat: '← Back to chat',
    display: {
      title: 'Display',
      description: 'What you see in the chat by default.',
      developerMode: 'Show developer details',
      developerModeDescription: 'Reveal raw tool calls, diffs, bash commands, and JSON inputs inline. When off, every tool call gets a humanized one-line summary; you can still expand individual ones with the ▽.',
    },
    notifications: {
      title: 'Notifications',
      description: 'How you find out about agents you\'re not watching live.',
      tierA: 'Needs your action',
      tierADescription: 'Approvals and questions. Always shows a toast with sound in the app; this switch also sends a push to your devices.',
      tierB: 'Informational',
      tierBDescription: 'An agent finishes, errors, or gets stuck. Appears in the bell feed with a badge — no push, no sound.',
      tierC: 'System problems',
      tierCDescription: 'Something is broken (disk, a server down). Always reaches you via Telegram and push — this one can\'t be turned off.',
      sound: 'Notification sound',
      soundDescription: 'Plays a short chime when an agent needs your action — even while you\'re watching the dashboard.',
      pushDevice: 'Push on this device',
      pushDeviceDescription: 'Get notifications on this device even with the app closed.',
      pushEnable: 'Enable',
      pushDisable: 'Disable',
      pushUnsupported: 'This browser can\'t receive push. On iPhone, install the app first: Share → Add to Home Screen.',
      pushDenied: 'Notifications are blocked for this app. Allow them in your device settings and come back.',
      testSend: 'Send test notification',
      testSending: 'Sending…',
      testDisabledHint: 'Turn on push for this device first, then send a test.',
      testSent: 'Sent — check your device.',
      testRateLimited: 'Wait a few seconds before sending another.',
      testFailed: 'Could not send the test. Try again.',
      telegram: 'Send notifications to Telegram',
      telegramDescription: 'Mirror agent updates to your Telegram group. System problems always go to Telegram regardless of this switch.',
    },
    permissions: {
      title: 'Default permission mode',
      description: 'How new agents handle tool execution by default. You can override per-agent with the mode chip.',
    },
    allowlist: {
      title: 'Allowed forever',
      description: 'Things you\'ve told Claude it can do without asking again. Forget any of them and the agent will ask the next time.',
      loading: 'Loading…',
      couldNotLoad: 'Could not load saved permissions',
      empty: 'You haven\'t allowed anything forever yet. Click "Allow always for…" on a permission card to save a rule here.',
      forget: 'Forget',
      forgetting: 'Forgetting…',
      couldNotForget: 'Could not forget this rule',
      showAll: 'Show all ({count})',
      toolKind: {
        Bash: 'Run command',
        Read: 'Read file',
        Edit: 'Edit file',
        Write: 'Write file',
        MultiEdit: 'Edit file',
        NotebookEdit: 'Edit notebook',
        Glob: 'Find files',
        Grep: 'Search code',
        WebFetch: 'Fetch URL',
      },
    },
    language: {
      title: 'Language',
      description: 'Choose the display language for the dashboard.',
      english: 'English',
      spanish: 'Español',
    },
    account: {
      title: 'Account',
      description: 'Your account settings.',
    },
    servers: {
      title: 'Servers',
      description: 'VPS instances running office agents.',
      addServer: 'Add server',
      name: 'Name',
      baseUrl: 'Base URL',
      id: 'Server ID',
      online: 'Online',
      offline: 'Offline',
      remove: 'Remove',
      save: 'Save',
      cancel: 'Cancel',
      empty: 'No servers configured yet. Add a server to start managing agents across multiple VPS.',
      namePlaceholder: 'VPS 1',
      baseUrlPlaceholder: 'https://vps1.asoasis.com',
      idPlaceholder: 'vps1',
    },
    projects: {
      title: 'Projects',
      description: 'Code repositories managed by agents.',
      addProject: 'Add project',
      name: 'Name',
      slug: 'Slug',
      workdir: 'Working directory',
      defaultBranch: 'Default branch',
      archive: 'Archive',
      unarchive: 'Restore',
      empty: 'No projects yet. Projects are created automatically when agents start.',
      save: 'Save',
      cancel: 'Cancel',
    },
    // Sprint 20.3 PR-8 — admin runners panel.
    runners: {
      title: 'Runners',
      description: 'Servers that host the Claude CLI for agents.',
      empty: 'No runners registered yet.',
      loadError: 'Could not load runners.',
      refresh: 'Refresh',
      reload: 'Refresh runners',
      // Column labels
      colHostname: 'Hostname',
      colStatus: 'Status',
      colAccount: 'Account',
      colCap: '5h used',
      colWeeklySonnet: 'Sonnet · this week',
      colWeeklyOpus: 'Opus · this week',
      colActive: 'Active',
      colLastSeen: 'Last seen',
      colVersion: 'Version',
      colActions: 'Actions',
      // Status badges
      statusOnline: 'Online',
      statusOffline: 'Offline',
      statusDraining: 'Draining',
      statusIncompatible: 'Incompatible version',
      // Actions
      drain: 'Drain',
      undrain: 'Undrain',
      reEnable: 'Re-enable',
      // Confirm modal
      confirmDrainTitle: 'Drain {hostname}?',
      confirmDrainBody: 'Drain stops new agents from being scheduled to {hostname} and waits for in-flight agents to finish before the runner exits cleanly.',
      confirmDrain: 'Drain runner',
      // Per-row banner when unavailable_reason is set
      unavailableReasonBlocked: 'Claude account blocked. Resolve with Anthropic, then re-enable.',
      unavailableReasonDiskFull: 'Disk full. Reclaim space on the runner, then re-enable.',
      unavailableReasonNotAuthed: 'Claude not authenticated. Run `claude login` on the runner, then re-enable.',
      unavailableReasonIncompatible: 'Runner is on an incompatible protocol version. Deploy the matching runner build, then re-enable.',
      // Sprint runner-resilience-after-missing-credential §2.4.
      unavailableReasonMissingCredential: 'A 1Password credential failed the runner’s boot healthcheck. Check the runner log for failing item titles and resolve via OFM. The runner auto-recovers within ~10 min once the issue is fixed.',
      // Sprint runner-op-token-single-source (defect 3) — the runner’s OP
      // token authenticates to the wrong 1Password workspace.
      unavailableReasonWrongWorkspace: 'The runner’s 1Password token authenticates to the wrong workspace. Expected santi-team.1password.com. Operator must rotate /etc/asoasis/op-token via the OFM resync mechanism (see runbook).',
      // Toasts
      drainSuccess: '{hostname} is draining.',
      drainError: 'Could not drain {hostname}: {reason}',
      undrainSuccess: '{hostname} is back in rotation.',
      undrainError: 'Could not undrain {hostname}: {reason}',
      clearUnavailableSuccess: '{hostname} re-enabled.',
      clearUnavailableError: 'Could not re-enable {hostname}: {reason}',
      // Misc
      capUnknown: '—',
      lastSeenNever: 'never',
      hours: '{value}h',
      hoursOfLimit: '{value}h / {limit}h',
      activeAgents: '{count}',
    },
  },

  // ── System pills ───────────────────────────────────────────────────
  system: {
    agentStarted: 'Agent started',
    newTurn: 'New turn',
    turnComplete: 'Turn complete',
    turnCompleteWithDuration: 'Turn complete · {duration}',
    agentExited: 'Agent {status}',
    switchedTo: 'Switched to {model} · {effort}',
    configChanged: 'Configuration changed',
    stopped: 'Stopped',
    agentCrashed: 'Agent crashed',
    reopenedConversation: 'Reopened conversation',
    readyContinuing: 'Ready — continuing',
    // Sprint stdout-transport-replay (D013-EC-118) — loud, visible
    // copy for verified transport loss / truncation. No jargon (no
    // "WebSocket", no "seq"): plain cause + what to do next.
    outputLost:
      'Part of the agent’s reply was lost due to a connection drop — ask the agent to repeat its last answer',
    outputTruncated:
      'A message from the agent was too large to deliver — ask the agent to resend it in smaller parts',
  },

  // ── Error card ─────────────────────────────────────────────────────
  error: {
    agentFailed: 'Agent failed',
    agentFailedWithReason: 'Agent failed: {reason}',
  },

  // ── Spawn errors (Sprint S20.5 PR-3 / D013-EC-69) ─────────────────
  // Founder-mode copy for the toast that fires when `POST /api/agents`
  // (or the lazy first-message spawn) is rejected by the backend with
  // a structured `code`. Each branch tells the founder what to DO next
  // in plain language; the generic fallback is the legacy string and
  // is preserved here for unrecognised codes.
  errors: {
    spawn: {
      NO_RUNNER_AVAILABLE:
        'No servers available. Open Settings \u2192 Servers to see what\u2019s offline.',
      RUNNER_NOT_READY:
        'The server is starting up. Try again in a few seconds.',
      CLAUDE_NOT_AUTHENTICATED:
        'Claude isn\u2019t signed in on this server. Ask the operator to run `claude /login` on the server.',
      RUNNER_OFFLINE:
        'The server for this agent is offline. Try again or pick another server.',
      GENERIC: 'Could not create the conversation. Tap to retry.',
    },
  },

  // ── Agent state banners ──────────────────────────────────────────
  agentBanner: {
    stuckTitle: 'No activity in {duration}',
    stuckWait: 'Wait',
    stuckStop: 'Stop',
    errorTitle: 'Agent stopped after {reason}',
    errorRetry: 'Retry',
    errorFork: 'Fork from last working turn',
    interruptedTitle: 'Stopped after {duration}',
    interruptedResume: 'Resume',
    interruptedFork: 'Fork',
  },

  // ── Network / system banners ─────────────────────────────────────
  networkBanner: {
    reconnecting: 'Reconnecting',
    disconnected: 'Disconnected — agents keep running on the server',
    disconnectedRetry: 'Retry',
    memoryConstrained: 'Server memory is tight — new agents disabled',
    // Sprint rate-limit-banner-ssot — per-runner copy.
    // {window} resolves to "5h", "weekly", "weekly Sonnet", "weekly Opus" or "pay-as-you-go".
    // {time} is the wall-clock time (e.g. "05:00 AM") of the earliest reset.
    claudeMaxOneRunner: 'Hit Claude Max {window} limit on {hostname}. Resets at {time}.',
    claudeMaxSomeRunners: 'Hit Claude Max {window} limit on {count} runners. Earliest reset at {time}.',
    claudeMaxAllRunners: 'Hit Claude Max {window} limit on all runners. Earliest reset at {time}.',
    windowFiveHour: '5h',
    windowSevenDay: 'weekly',
    windowSevenDaySonnet: 'weekly Sonnet',
    windowSevenDayOpus: 'weekly Opus',
    windowExtraUsage: 'pay-as-you-go',
    viewUsage: 'View usage',
  },

  // ── Spawn preparing toast (Sprint 20.3 PR-5) ─────────────────────
  // Founder-mode auto-clone surface. Displayed below the chat when
  // the runner is preparing the project clone before the first
  // `agent.spawn_request`. Failure copy is owned by the backend
  // (`founderCopyForCloneFailure()` in session-manager.ts) so the
  // backend `errorMessage` field on the agent matches the toast.
  spawnPreparingToast: {
    failedTitle: 'Project setup failed',
    retry: 'Retry',
    dismissAria: 'Dismiss notification',
    dismissGlyph: '×',
  },

  // ── Runner-unavailable failover banner ────────────────────────────
  // S20.3 PR-6 introduced the offline variant; auto-failover-on-rate-limit
  // (D013-EC-109) extends with the rate-limited variant. Same banner
  // pattern, two discriminated copies. Founder-mode: plain language —
  // NEVER "rate-limit" or "weekly cap" in the user-visible surface.
  runnerUnavailableBanner: {
    // Per-variant copy.
    offline: {
      title: '{runner} is offline',
      subtitle: "Your agent's runner is unreachable. Try another runner to keep going.",
      tryAnotherRunner: 'Try another runner',
    },
    rateLimited: {
      title: '{runner} reached its weekly quota',
      subtitle:
        "Your agent's runner has no weekly quota left. Move this conversation to another runner to keep going.",
      tryAnotherRunner: 'Move to another runner',
    },
    // Shared affordance copy.
    busyLabel: 'Reassigning…',
    dismissAria: 'Dismiss',
    dismissGlyph: '×',
    successToast: 'Reassigned to {to}.',
    errorToast: 'Failover failed: {reason}',
    genericError: 'Failover failed.',
  },

  // Sprint 20.5 PR-2 (D013-EC-68).
  sessionLostBanner: {
    title: 'This session ended',
    subtitle: 'Tap retry to resume the conversation.',
    retry: 'Retry',
    busyLabel: 'Resuming…',
    dismissAria: 'Dismiss session-ended banner',
    dismissGlyph: '×',
    successToast: 'Conversation resumed.',
    errorToast: 'Could not resume: {reason}',
    genericError: 'Could not resume.',
  },

  // Sprint 20.6 PR-2 (D013-EC-72) — Anthropic dropped the resumed
  // sessionId. Resume is impossible by definition; the next-best
  // founder action is a fresh agent.
  sessionExpiredBanner: {
    title: 'This session expired',
    subtitle: 'Anthropic dropped the conversation context. Start a new agent to keep going.',
    newAgent: 'Start a new agent',
    dismissAria: 'Dismiss session-expired banner',
    dismissGlyph: '×',
  },

  // Sprint 20.6 PR-2 (D013-EC-72) — non-blocking 2 s toast that lets
  // the user know the parpadeo they just saw was a deliberate respawn,
  // not a crash.
  respawnedToast: {
    message: 'Conversation resumed',
  },

  // ── Live activity (Sprint 17) ──────────────────────────────────────
  // Single in-flight indicator below the last message. Surface for the
  // founder is intentionally short: actor + verb + state. The headline
  // for `working` is the same `tool_use.input.description` (or verb-map
  // fallback) the persisted ToolCallRow shows.
  liveActivity: {
    thinking: 'Thinking',
    working: 'Working',
    ariaLabel: 'Agent activity',
  },

  // ── Todo checklist ─────────────────────────────────────────────────
  todos: {
    heading: 'Todos',
    empty: 'TodoWrite (no items)',
    progress: '{completed}/{total} done',
    cancelled: '{count} cancelled',
  },

  // ── Plan mode banner ───────────────────────────────────────────────
  planMode: {
    banner: 'Plan mode — research only, no file changes',
  },

  // ── Subagent panel ─────────────────────────────────────────────────
  subagent: {
    label: 'Subagent',
    tools: '{count} tool',
    toolsPlural: '{count} tools',
    running: '(working…)',
    error: '(failed)',
    prompt: 'Prompt',
    subagentError: 'Subagent error',
    finalResult: 'Final result',
    emptyResult: '(empty)',
    // Sprint ai-elements-rendering — recursive subagent tree.
    depthBadge: 'N{depth}',
    descendantTools: '(+{count})',
    orphanNotice: 'Subagent output without its parent call (history truncated)',
  },

  // ── Dual mode chip ─────────────────────────────────────────────────
  dualMode: {
    label: 'Developer mode',
    disableTooltip: 'Disable developer mode',
  },

  // ── Permissions ────────────────────────────────────────────────────
  permissions: {
    agentWantsTo: '{agent} wants to {action}',
    allowOnce: 'Allow once',
    allowAlways: 'Allow always',
    deny: 'Deny',
    // Bash intents
    deleteFolderNamed: '{agent} wants to delete the folder {name}',
    deleteFolder: '{agent} wants to delete a folder',
    deleteFileNamed: '{agent} wants to delete {name}',
    deleteFiles: '{agent} wants to delete files',
    pushToGithub: '{agent} wants to push to GitHub',
    discardChanges: '{agent} wants to discard local changes',
    changePermissions: '{agent} wants to change file permissions',
    changeOwnership: '{agent} wants to change file ownership',
    runAsAdmin: '{agent} wants to run a command as administrator',
    downloadFromHost: '{agent} wants to download from {host}',
    downloadFromInternet: '{agent} wants to download from the internet',
    installPackage: '{agent} wants to install {package}',
    stopProcess: '{agent} wants to stop a running process',
    runCommand: '{agent} wants to run a command',
    modifyFileNamed: '{agent} wants to modify {name}',
    modifyFile: '{agent} wants to modify a file',
    fetchFromHost: '{agent} wants to fetch from {host}',
    fetchFromInternet: '{agent} wants to fetch from the internet',
    searchWeb: '{agent} wants to search the web',
    finishedPlanning: '{agent} finished planning and wants to start',
    sensitiveAction: '{agent} wants to run a sensitive action',
    // Bodies
    insideDir: 'Inside {dir}.',
    pushDetails: 'Push {branch} to {remote}.',
    hardResetDetails: 'Hard-reset to {ref}. Any uncommitted edits will be lost.',
    forceCheckoutDetails: 'Force-checkout — any uncommitted edits will be lost.',
    sudoDetails: 'sudo elevates privileges system-wide.',
    usingTool: 'Using {tool}.',
    openDevDetails: 'Open developer details to see the exact command.',
    commandStartsWith: 'Starts with {verb}. Open developer details to see the exact command.',
    planToAgentBody: 'Approve to switch from Plan mode to Agent mode for the rest of this turn.',
    toolLabel: 'Tool: {tool}. Open developer details to see the exact request.',
    showRawDetail: 'Show what Claude is about to run',
    askingPermission: 'Claude is asking for permission',
    addsGlobalRule: 'Adds a global rule: {rule}',
    couldNotSave: 'Could not save your decision',
    requestGone: 'This request was already resolved.',
    couldNotSaveDetail: 'Could not save your decision: {error}',
    allowAlwaysFor: 'Allow always for',
  },

  // ── Error boundary ─────────────────────────────────────────────────
  errorBoundary: {
    title: 'Something broke',
    body: 'The dashboard hit an unexpected error and stopped rendering. Your agents on the server are unaffected — this is only the browser view.',
    reload: 'Reload',
    copyDetails: 'Copy details',
    crashReport: 'Venuz office crash report',
  },

  // ── Protected route ────────────────────────────────────────────────
  protectedRoute: {
    loading: 'Loading dashboard…',
  },

  // ── Autocomplete dropdown ──────────────────────────────────────────
  autocomplete: {
    noResults: 'No results',
    loading: 'Searching…',
    error: 'Could not load suggestions',
  },

  // ── Shared / common ────────────────────────────────────────────────
  common: {
    cancel: 'Cancel',
    retry: 'Retry',
    close: 'Close',
    save: 'Save',
    edit: 'Edit',
    resend: 'Resend',
    searchNoMatch: 'No agents match "{query}"',
    refresh: 'Refresh',
  },

  // ── Tooltips ───────────────────────────────────────────────────────
  tooltip: {
    newAgent: 'New agent',
    searchAgents: 'Search agents',
    archive: 'Archive',
    restore: 'Restore — move back to active',
    deletePermanently: 'Delete permanently',
    moreActions: 'More actions',
    stopAgent: 'Stop agent',
    notifications: 'Notifications',
    settings: 'Settings',
    account: 'Account',
  },

  // ── Sticky / user message ──────────────────────────────────────────
  message: {
    edit: 'Edit',
    resend: 'Resend',
  },

  // ── Scroll to bottom ───────────────────────────────────────────────
  scrollToBottom: {
    label: 'Scroll to bottom',
    newMessage: '{count} new message',
    newMessages: '{count} new messages',
    ariaLabel: '{count} new messages, scroll to bottom',
  },

  // ── Chat view ─────────────────────────────────────────────────────
  chatView: {
    loadingMessages: 'Loading messages…',
    couldNotLoadMessages: 'Could not load messages: {error}',
    noMessagesYet: 'No messages yet — the agent will reply shortly.',
    loadingOlder: 'Loading older messages…',
  },

  // ── Message bubble ────────────────────────────────────────────────
  messageBubble: {
    empty: '(empty)',
    toolError: 'Tool error',
    toolResult: 'Tool result',
  },

  // ── Partial indicator ─────────────────────────────────────────────
  partialIndicator: {
    ariaLabel: 'Agent is typing',
  },

  // Sprint 20.3 PR-7 (D013-EC-22) — live-stream truncated pill.
  streamTruncated: {
    label: '[truncated · reload to refetch]',
    ariaLabel: 'Live stream truncated — click to reload from server',
    icon: '⚠',
  },

  // ── Search bar ────────────────────────────────────────────────────
  searchBar: {
    placeholder: 'Search agents…',
    ariaLabel: 'Search agents',
    clearAriaLabel: 'Clear search',
  },

  // ── Spinner ───────────────────────────────────────────────────────
  spinner: {
    ariaLabel: 'Loading',
  },

  // ── Agent context menu extra ──────────────────────────────────────
  agentActions: {
    ariaLabel: 'Agent actions',
  },

  // ── Permission card extra ─────────────────────────────────────────
  permissionCard: {
    ariaLabel: 'Agent is asking for permission',
    showDevDetails: 'Show what the agent is about to run',
  },

  // ── Aggregation card ──────────────────────────────────────────────────
  aggregation: {
    running: 'running',
    errored: '{count} errored',
    completed: 'completed',
  },

  // ── Turn summary card ─────────────────────────────────────────────────
  turnSummary: {
    done: 'Done',
    errored: 'Errored',
    stopped: 'Stopped',
    modifiedFile: 'Modified {count} file',
    modifiedFiles: 'Modified {count} files',
    modifiedFileIn: 'Modified {count} file in {dir}',
    modifiedFilesIn: 'Modified {count} files in {dir}',
    typecheck: 'Type check',
    tests: 'Tests',
    lint: 'Lint',
    build: 'Build',
    passed: 'passed',
    failed: 'failed',
    showCommands: 'Show commands',
    hideCommands: 'Hide commands',
  },

  // ── Sticky progress bar ──────────────────────────────────────────────
  progress: {
    allDone: 'All steps done',
    working: 'Working…',
    phaseOf: 'Phase {current} of {total}',
  },

  // ── Todo update pill ─────────────────────────────────────────────────
  todoUpdate: {
    started: 'Started',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },

  // ── Ask user question card ───────────────────────────────────────────
  askQuestion: {
    heading: 'Claude needs your input',
    pickOption: 'Pick an option below to continue.',
    answerQuestions: 'Answer {count} questions to continue.',
    submit: 'Submit',
    pickAny: '(pick any)',
    noOptions: 'No options provided.',
    askingQuestion: 'Claude is asking a question',
    couldNotSend: 'Could not send your answer',
    alreadyAnswered: 'This question was already answered.',
    couldNotSendDetail: 'Could not send your answer: {error}',
  },

  // ── Permission outcome pill ──────────────────────────────────────────
  permissionOutcome: {
    autoAllowed: 'Auto-allowed:',
    allowed: 'Allowed:',
    denied: 'Denied:',
    decidedBy: 'Decided by:',
    reason: 'Reason:',
    allowlistPattern: 'Allowlist pattern:',
    tool: 'Tool:',
  },

  // ── Agent list item ──────────────────────────────────────────────────
  agentList: {
    working: 'Working…',
    waitingForInput: 'Waiting for input',
    noActivity: 'No activity in {min}m',
    done: 'Done',
    diffStat: '+{added} / -{removed} · {files} files',
    // Sprint UI-7b — variant used when linesRemoved is 0; we drop the
    // "-0" segment so the sidebar row doesn't carry a meaningless zero.
    diffStatNoRemoved: '+{added} · {files} files',
    unread: 'Unread',
    renameAgent: 'Rename agent',
    clearSearch: 'Clear search',
  },

  // ── Mode chip ────────────────────────────────────────────────────────
  modeChip: {
    agent: 'Autonomous',
    plan: 'Plan',
    auto: 'Autonomous',
    agentDesc: 'Runs without asking. Dangerous operations are blocked automatically.',
    planDesc: 'Read-only — explores and plans without changing files',
    autoDesc: 'Runs without asking. Dangerous operations are blocked automatically.',
    modeAriaLabel: 'Permission mode: {mode}. Click to change.',
  },

  // ── Model/effort chips ───────────────────────────────────────────────
  modelChip: {
    modelAndEffortAriaLabel: 'Model: {model}, effort: {effort}. Click to change.',
    popoverAriaLabel: 'Pick model and effort',
    opusOnlyTag: 'Opus only',
  },

  // ── Branch chip ──────────────────────────────────────────────────────
  branch: {
    ahead: '{count} ahead',
    behind: '{count} behind',
    // Sprint UI-7b (D76) — ahead/behind relative to origin/main (distinct
    // from the branch's own upstream sync). Used by AheadBehindChip.
    aheadOfMain: '{count} ahead of main',
    behindOfMain: '{count} behind main',
    uncommittedChanges: 'uncommitted changes',
  },

  // ── Runner chip (Sprint 20.3 PR-9 / D20 / UC-38) ──────────────────────
  // Tooltip strings — Cursor-parity calm and structured, no emoji.
  // Order: `Runner-N · acct-id · cap NN%`.
  runnerChip: {
    tooltipFull: '{hostname} · {account} · {cap}',
    tooltipNoCap: '{hostname} · {account}',
    tooltipMinimal: '{runner} · usage not yet reported',
    cap: 'used {value}%',
    accountUnknown: 'account not reported',
  },

  // ── Context donut ────────────────────────────────────────────────────
  contextDonut: {
    ariaLabel: 'Context used {pct}%',
    defaultTooltip: 'Context used: {pct}%\nDerived at each turn boundary from the CLI\'s usage counters.',
  },

  // ── Runners-health pill (sidebar header) ─────────────────────────────
  // Sprint UI compaction (2026-05-11) — replaces the old `sidebar.heading`
  // ("Agents") + the vestigial `ServerSelector` chip. The pill is the
  // sidebar header's only label now. See `RunnersHealthPill.tsx`.
  runnersHealth: {
    ariaLabel: 'Runner status — click to see capacity details',
    // Plural-count label, all online + cap healthy:
    //   "2 runners"           (without cap segment)
    //   "2 runners · 78% free" (allHealthyWithCap)
    allHealthy: '{count} runners',
    allHealthyWithCap: '{count} runners · {cap}',
    // Singular variant
    singleRunner: '1 runner',
    singleRunnerWithCap: '1 runner · {cap}',
    // Mixed online/offline state
    degraded: '{online}/{total} runners',
    // Zero online — cannot spawn anything
    allOffline: 'No runners online',
    // Cap fragment substituted into the labels above
    cap: {
      percent: '{value}% free',
    },
    tooltip: {
      title: 'Runner capacity',
      // Row when the runner is online and reports cap
      capFree: '{value}% free',
      // Row when the runner is online but has not sent a cap-bearing
      // heartbeat yet (newly provisioned, D013-EC-60)
      capUnknown: 'cap unknown',
      // Row when offline / draining / incompatible
      unavailable: 'unavailable',
      // Edge case: no runners at all in the store
      empty: 'No runners registered yet.',
    },
  },

  // ── Usage modal ──────────────────────────────────────────────────────
  // Sprint 20.3 PR-10 — repurposed from per-account OAuth aggregate
  // (which only made sense pre-S20.2 single-runner) to per-runner
  // aggregation across the whole topology. D17.
  usage: {
    title: 'Runner usage',
    description: 'Capacity and utilization across every runner in the deployment.',
    loading: 'Fetching usage…',
    error: 'Could not fetch runner usage. Try again in a few minutes.',
    retry: 'Retry',
    empty: 'No runners registered yet.',
    // Per-runner table column headers
    colRunner: 'Runner',
    colAccount: 'Account',
    colCap: '5h used',
    colWeeklySonnet: 'Sonnet · this week',
    colWeeklyOpus: 'Opus · this week',
    colActive: 'Active',
    colStatus: 'Status',
    colLastSeen: 'Last seen',
    // Status badge labels (mirror RunnersTable.statusOnline/Offline/...)
    statusOnline: 'Online',
    statusOffline: 'Offline',
    statusDraining: 'Draining',
    statusIncompatible: 'Incompatible',
    // Footer
    footerEarliestReset: 'Earliest reset: {hostname} in {time}',
    footerNoReset: 'No cap reset reported yet.',
    // Per-bar caption next to the percentage and below the bar
    percent: '{value}%',
    resetsIn: 'Resets in {time}',
    // Cell fallback
    capUnknown: '—',
    hoursOfLimit: '{value}h / {limit}h',
    // Sprint 20.8 (D013-EC-76) — bar labels for the five canonical
    // Anthropic buckets. Same wording Claude Code uses in its own
    // Account & Usage panel (Cursor parity per cursor-ux-mirror.md).
    session5h: 'Session (5h)',
    weekly7d: 'Weekly (7d)',
    weeklySonnet: 'Sonnet weekly',
    weeklyOpus: 'Opus weekly',
    extraUsage: 'Extra usage',
    // Sprint usage-limits-v2 (2026-07-08) — labels for the canonical
    // `limits[]` array Anthropic now surfaces. `limitWeeklyScopedModel`
    // is used whenever the entry carries a per-model display name
    // (e.g. "Fable"); the un-scoped fallback covers the rare case where
    // `weekly_scoped` arrives without a model name.
    limitSession: 'Session (5h)',
    limitWeeklyAll: 'Weekly (7d)',
    limitWeeklyScoped: 'Weekly · scoped',
    limitWeeklyScopedModel: 'Weekly · {model}',
    // Red-state sub-label when a limit is active or has hit 100 %.
    limitReached: 'Limit reached — resets in {time}',
    // Per-runner failure copy (founder-mode). Each maps to a
    // `usageErrorCode` returned by the bridge. Keep these declarative
    // + actionable: every message says what happened and what to do
    // next.
    errorOauthMissing: 'Claude credentials not found on {hostname}. Run `claude login` on the runner.',
    errorOauthInvalid: 'Claude token expired on {hostname}. Run `claude login` on the runner.',
    errorRateLimited: 'Anthropic rate-limited the usage endpoint for {hostname}. Try again in a few minutes.',
    errorUpstream: 'Could not fetch usage from {hostname}. Try again in a moment.',
    errorTimeout: '{hostname} did not reply in time. The runner is likely overloaded — try again shortly.',
    errorRunnerOffline: 'Runner offline since {lastSeen}. Reconnect to see usage.',
  },

  // ── Tool call block ──────────────────────────────────────────────────
  toolCall: {
    running: '(running…)',
    error: '(error)',
    args: 'Args',
    errorLabel: 'Error',
    result: 'Result',
    empty: '(empty)',
    showLess: 'Show less',
    showMore: 'Show more ({chars} chars)',
    inputs: 'Inputs',
    // Sprint monitor-tool-live-timer — inline elapsed counter for tool
    // calls still in flight (used by Monitor / any tool that exceeds the
    // 2 s threshold). Format mirrors Cursor's "Monitored background tasks
    // · Waiting Xs" pattern.
    waitingSeconds: 'Waiting {s}s',
    waitingMinutes: 'Waiting {m}m {s}s',
    waitingHours: 'Waiting {h}h {m}m',
  },

  // ── Doc viewer (D-012, sprint md-doc-viewer; D-019 download/binary) ─
  doc: {
    loading: 'Loading…',
    notFound: '{path} no longer exists in this project.',
    tooLarge: 'This doc is larger than 25 MB.',
    forbidden: 'Path is not allowed.',
    sessionExpired: 'Session expired. Please reload.',
    networkError: 'Could not load this doc. Check your connection.',
    retry: 'Retry',
    close: 'Close',
    download: 'Download',
    noWorkdir: 'This agent has no working directory yet.',
    pdfIosFallback: 'Preview not available on iOS. Tap Download to open this PDF.',
  },

  // ── AI Elements rendering surface (sprint ai-elements-rendering) ─
  aiElements: {
    chainOfThought: {
      header: 'Thinking steps',
    },
    reasoning: {
      thinking: 'Thinking…',
      thoughtBriefly: 'Thought for a few seconds',
      thoughtForSeconds: 'Thought for {duration} seconds',
    },
    codeBlock: {
      copy: 'Copy',
      copied: 'Copied',
      copyAria: 'Copy code to clipboard',
      copiedAria: 'Code copied to clipboard',
    },
    attachments: {
      image: 'Image',
      file: 'Attachment',
      remove: 'Remove attachment',
      uploadFiles: 'Upload files',
      addPhotosOrFiles: 'Add photos or files',
      errorAccept: 'No files match the accepted types.',
      errorMaxFileSize: 'All files exceed the maximum size.',
      errorMaxFiles: 'Too many files. Some were not added.',
    },
    promptInput: {
      placeholder: 'What would you like to know?',
      submit: 'Send',
    },
  },

  // ── Oficina Virtual F1 (spec 001 T12) — /office screen ─────────────────
  office: {
    // Oficina vieja (isometrica/Habbo) ELIMINADA del codigo por orden de Pau
    // (07-09-2026, "se tira a la basura"). Quedan solo las claves que usan
    // componentes vivos del panel: boton de la barra, acceso desde el chat
    // vacio y el resumen plegado del despertador en UserMessage.
    title: 'Virtual office',
    navLabel: 'Virtual office',
    homeAccess: '🏢 Office',
    wake: {
      collapsed: '⏰ Wake alarm · {summary}',
    },
  },
  oficinaV2: {
    versionNueva: 'A new version of the panel is available.',
    recargar: 'Reload',
    titulo: 'Office',
    abriendo: 'Opening the office…',
    errorCarga: 'Could not load the office. Retrying on reconnect.',
    sinConexion: 'Offline · reconnecting…',
    desconocido: 'Unnamed',
    sinTitulo: 'New assignment',
    contratar: 'Hire an agent',
    contratarCorto: 'Hire',
    galeria: {
      titulo: 'Photos and files',
      verTodo: 'See all ({n})',
      volver: 'Back to the card',
      filtro: 'Who sent it',
      todos: 'All',
      tuyos: 'Yours',
      suyos: 'Theirs',
      nada: 'Nothing here.',
      deTi: 'You',
      noDisponible: 'No longer available',
    },
    buscar: {
      titulo: 'Search the conversation',
      abrir: 'Search the conversation',
      placeholder: 'Search words in this chat…',
      cerrar: 'Close search',
      pista: 'Type at least two letters. It searches the whole conversation, ignoring case and accents.',
      buscando: 'Searching…',
      nada: 'No message with «{q}».',
      uno: '1 message',
      varios: '{n} messages',
      tu: 'You',
    },
    tiempo: { ahora: 'now', min: '{n} min ago', h: '{n} h ago', d: '{n} d ago' },
    vivo: {
      paso: 'Step {n}',
      haceS: '{n} s ago',
      herramienta: {
        mirar: 'Going through files',
        internet: 'Searching the web',
        cambiar: 'Making changes',
        comprobar: 'Running checks',
        ayudante: 'With help from another agent',
        tareas: 'Organising its tasks',
        escribiendote: 'Writing to you',
        apuntando: 'Noting how it is going',
        permiso: 'Asking for your permission',
        pregunta: 'Preparing a question for you',
        plan: 'Preparing its plan',
        informe: 'Writing its report',
        cerrando: 'Packing up to leave',
        companeros: 'Checking who else is working',
        oficina: 'Talking to the office',
        otra: 'Working',
      },
    },
    resumen: {
      linea: '{trabajando} working',
      teNecesitaUno: '{n} needs you',
      teNecesitanVarios: '{n} need you',
      trabajando: '{n} working',
      teNecesita: '{n} needs you',
      teNecesitan: '{n} need you',
    },
    equipo: {
      teEsperaUno: '1 is waiting for you',
      teEsperan: '{n} are waiting for you',
      nadaMasTeToca: 'Nothing else needs you now',
      plegar: 'Collapse the list',
      masOpciones: 'More options',
      cuentasYAccesos: 'Accounts and access',
      desplegar: 'Open the list',
      subidasRiel: '{n} deploys waiting for your Allow: open the list',
      contratarAlguien: 'Hire someone',
      sinLeer: 'Unread',
      teNecesitan: 'Need you',
      problemas: 'With a technical fault',
      bloqueados: 'Stopped by a problem',
      trabajando: 'Working',
      enSuMesa: 'Idle',
      vacioTitulo: 'Your office is ready',
      accion: {
        plan: 'Wants your go-ahead to start',
        pregunta: 'Has a question for you',
        permiso: 'Asks you for permission',
        integrar: 'Finished: is the work good?',
        atasco: "Couldn't finish: take a look",
        bloqueo: 'Waiting on you to continue',
      },
      titulo: 'Team',
      cuantos: '{n} in the office',
      vacio: "Hire someone: they'll read your request and explain how they'll do it before touching anything.",
    },
    // 04-11 (Delivery 3) — the list in blocks: Your turn · Finished · In progress · With Santi › · Parked › · Leaving ›.
    lista: {
      bloque: {
        te_toca: 'Your turn',
        han_terminado: 'Finished',
        en_marcha: 'In progress',
        con_santi: 'With Santi',
        aparcados: 'Parked',
        se_van: 'Leaving',
      },
      resumen: {
        enMarcha: '{n} in progress',
        siguiente: 'next',
        atajo: 'Alt ↓',
        irA: 'Go to «{bloque}»',
        subidaUna: '1 deploy',
        subidaVarias: '{n} deploys',
      },
      accion: {
        revisar: 'Review',
        revisarAria: 'Review the deploy from {nombre}: {que}',
        cerrar: 'Close',
        cerrarAria: 'Close the work of {nombre}',
        cerrarLos: 'Close all {n}',
        cerrarLosAria: 'Close the {n} finished jobs',
      },
      cierre: {
        uno: 'Closing «{nombre}»',
        varios: 'Closing {n} jobs',
        deshacer: 'Undo',
        error: 'Could not close «{nombre}»: {motivo}',
      },
      subida: {
        quiere: 'Wants to deploy: {que}',
        revisarLaSubida: 'Review the deploy',
        etiqueta: 'Waiting for your Allow',
      },
      aparcado: {
        solo: 'Parked {cuando}',
        porPau: 'You parked it {cuando}',
        proponerCerrar: 'Parked for {n} days · close it?',
        paraCerrar: '{n} to close',
        aparcar: 'Park',
        despertar: 'Wake up',
        aparcadoAviso: '«{nombre}» parked: it will not alert you until something new happens',
        despertadoAviso: '«{nombre}» awake',
        error: 'Could not do it: {motivo}',
        deshacer: 'Undo',
      },
      santi: {
        nuevo: '1 new',
        nuevos: '{n} new',
        abrir: 'Open Santi',
      },
    },
    estado: {
      llegando: 'Starting…',
      trabajando: 'Working',
      esperando: 'Idle: message it if you need something',
      cerrando: 'Finishing',
      integrando: 'Merging their work (then carries on)',
      bloqueado: 'Stopped by a problem',
      error: 'Has a technical fault',
    },
    teNecesita: {
      plan: 'Give the go-ahead to start',
      pregunta: 'Has a question for you',
      permiso: 'Asks for permission',
      integrar: 'Finished: is the work good?',
      atasco: "Couldn't finish",
      bloqueo: 'Waiting on you',
    },
    puesto: {
      porDecidir: 'Role not decided yet',
      trabajaraComo: '{puesto}',
    },
    fase: {
      orientandose: 'Reading your request',
      esperando_contrato: 'Waiting for your go-ahead',
      trabajando: 'Working',
      verificando: "Checking it's right",
      bloqueado: 'Stopped by a problem',
      cerrando: 'Finishing',
      terminado: 'Done',
    },
    saltar: {
      atajo: 'Ctrl K',
      esc: 'Esc',
      titulo: 'Jump to an agent',
      placeholder: 'Search or jump to an agent',
      ayuda: '↑ ↓ to move · Enter to open · with nothing typed, the first is the one waiting longest for you',
      contratar: 'Hire someone',
      contratarPara: 'Hire someone for “{texto}”',
      sala: 'Open the Control Room',
      boton: 'Search or jump to an agent (Ctrl K)',
    },
    escena: {
      acciones: 'Actions for {nombre}',
      sala: 'Room {sala}',
      sinSala: '{nombre} (no room)',
      personaje: '{nombre}, {puesto}, in {sala}. {estado}',
      mesaLibre: 'Free desk in {sala}: hire here',
      abrirChat: 'Open chat',
      nombreConEstado: '{nombre} · {estado}',
      corto: { te_necesita: 'needs you', bloqueado: 'stopped', error: 'fault' },
    },
    // 04-11 — rooms as folders (orden.md rule 12): secondary rooms fold into their card when everything doesn't fit.
    carpetas: {
      abrir: 'Open the {sala} room',
      unAgente: '1 agent',
      agentes: '{n} agents',
      contratar: 'Hire here',
      contratarEn: '{sala} room is empty: hire here',
      plegar: 'Fold the {sala} room',
    },
    mesas: {
      moverDeMesa: 'Move to another desk',
      deshacer: 'Undo',
      movido: 'You moved {nombre} to desk {n}',
      cambiados: 'You swapped {a} and {b}',
      ordenar: 'Arrange',
      ordenarOficina: 'Arrange the office desks',
      listo: 'Done',
      aviso: 'Arranging the office: drag an agent to another desk in its room (if taken, they swap; with the keyboard, the arrow keys)',
      cambiarSitio: 'Swap seats',
      moverAqui: 'Sit here',
    },
    // 04-11 — desk groups ("team tables", web/qa/auditoria-apple/propuesta-2/fuentes/grupos.md).
    grupos: {
      sinGrupo: 'No group',
      moverA: 'Move to',
      grupoNuevo: 'New group…',
      nombreNuevo: 'New group',
      agruparLos: 'Group these {n}…',
      atajoAgrupar: 'Ctrl G',
      atajoSupr: 'Del',
      sugerido: 'recent',
      renombrarAgente: 'Rename',
      renombrar: 'Rename',
      renombrarPista: 'Rename the group (F2)',
      nombreAria: 'Group name',
      repetido: 'There is already a group called {nombre}',
      cartelAria: 'Group {nombre}, {n}',
      menu: 'More options for group {nombre}',
      contratarAqui: 'Hire someone in {nombre}',
      esperandoSanti: 'Waiting for Santi',
      esperarContrato: 'Wait for a contract with Santi',
      noEsperar: 'Stop waiting',
      sinContratos: 'There are no open contracts with Santi',
      abrirContrato: 'Open the contract "{titulo}"',
      duenoSeFue: 'The contract agent has left: the group is still waiting for Santi',
      duenoSeFueCorto: 'the contract agent has left',
      aparcarLos: 'Park these {n}',
      despertarLos: 'Wake these {n}',
      deshacer: 'Ungroup',
      deshechoAviso: 'Group {nombre} ungrouped: its {n} stay in the room',
      movidoA: '{nombre} is now in {grupo}',
      movidoFuera: '{nombre} is no longer in a group',
      otraSala: 'Each room is its own project: an agent never changes room',
      enGrupo: 'in group {nombre}',
      ver: 'View',
      todos: 'All',
      quitarFiltro: 'Remove the {nombre} filter',
      fueraUno: '{n} waiting for you outside',
      fuera: '{n} waiting for you outside',
      filtroVacio: 'Group {nombre} is now empty',
      contratoDe: 'contract "{titulo}"',
      pelotaDesde: 'Santi has had the ball since {cuando}',
      campo: 'Group',
      campoAyuda: 'The team table they sit at. Without a group, the first free desk.',
      opcionCampo: '{nombre} ({n})',
      opcionSugerida: '{nombre} (empty)',
      saltarN: '{n} in the group',
      saltarTe: '{te} of {n} waiting for you',
    },
    sala: {
      titulo: 'Room settings',
      ajustes: 'Settings for room {sala}',
      nombre: 'Name',
      color: 'Color',
      guardar: 'Save',
      quitar: 'Remove the room',
      quitarSi: 'Yes, remove it',
      quitarExplica: 'The room disappears from the office; its project and work are not deleted. Only possible if nobody is working in it.',
    },
    chat: {
      conversacion: 'Conversation',
      conversacionCon: 'Conversation with {nombre}',
      cerrar: 'Close the conversation',
      cerrarVentana: 'Close',
      masAcciones: 'More actions',
      volver: 'Back to the office',
      atras: 'Back to the list',
      sinAgente: 'This agent is no longer in the office.',
      cargando: 'Opening the conversation…',
      cargandoAnteriores: 'Loading earlier messages…',
      escribiendo: 'typing…',
      pasos: {
        uno: '1 work note',
        varios: '{n} work notes',
        trabajando: 'Working',
        abrir: 'See what it did',
        cerrar: 'Hide what it did',
      },
      traducido: 'Translated from English',
      verOriginal: 'See the original',
      enCorto: 'In short:',
      leerEntero: 'Read it all',
      leerMenos: 'Show less',
      leerMensaje: 'Read the whole message',
      ocultarMensaje: 'Hide the message',
      ahora: {
        trabajando: 'Working',
        teNecesita: 'Needs you',
        problema: 'Has a problem',
        parado: 'Idle',
        paradoSinMas: 'finished what it was doing and needs nothing from you',
        cerrando: 'The office is reviewing its work',
        empezando: 'Starting',
      },
      verTraduccion: 'See the translation',
      escribe: 'Write a message…',
      enviar: 'Send',
      adjuntar: 'Attach a file',
      quitarAdjunto: 'Remove {nombre}',
      subiendo: 'Uploading…',
      alFinal: 'Go to the latest message',
      hoy: 'Today',
      ayer: 'Yesterday',
      vozSinTexto: 'Voice note ({duracion}) · could not be transcribed',
      interrumpir: 'Interrupt what they are doing',
      interrumpiendo: 'Interrupting…',
      cerrandoSinMensajes: 'The office is reviewing its work to integrate it; it takes a few minutes. You cannot write to it meanwhile: a change now would go in unreviewed.',
      siguiente: {
        titulo: 'Next:',
        mas: '{n} more waiting for you',
        masUno: '1 more waiting for you',
        ir: 'Go',
        irA: 'Go to {nombre}: {situacion}',
      },
      entrega: {
        pendiente: 'Will be delivered as soon as possible',
        noEntregado: 'Not delivered',
        enviado: 'Sent',
        recibido: 'Received',
      },
      trabajo: {
        mirar: 'looked at things ({n})',
        cambiar: 'changed things ({n})',
        comprobar: 'ran checks ({n})',
        ayudantes: 'got help from other agents ({n})',
        otros: 'other tasks ({n})',
        fallos: 'hiccups along the way ({n})',
      },
      telegramEnviado: 'Also sent to you on Telegram.',
      telegramFallo: 'Could not also send it on Telegram.',
      noSePudoUno: 'Could not open 1 file',
      noSePudieronVarios: 'Could not open {n} files',
      hito: {
        consultaGithub: 'Checked something on GitHub',
        consultaAgentes: 'Checked which other agents are working now',
        sinCupo: 'Their server ran out of usage allowance',
        trasladado: 'Moved to another server with their conversation and work: they carry on where they left off',
        permisoAprobado: 'You gave them permission',
        permisoHecho: 'You gave them permission and the office did it',
        permisoNoSePudo: 'You gave them permission, but the office could not do it',
        permisoDenegado: 'You told them no',
        avisoOficina: 'The office sent them a notice',
        contratado: 'Started: reading your request',
        contratoAprobado: 'You gave the go-ahead',
        cambiosPedidos: 'You asked for changes: {texto}',
        respondiste: 'You answered: {texto}',
        cierrePedido: 'You asked it to finish',
        cierrePedidoAgente: 'They asked to leave themselves (not you)',
        integrarPedido: 'Asks to merge their work; then keeps working',
        integrado: 'Their work is in the official version; keeps working',
        integrarFallo: 'Their work could not be merged; keeps working',
        contratoPropuesto: 'Explained how it will do it',
        pregunta: 'Asked you a question',
        permiso: 'Asks you for a permission',
        notaEntrega: 'Left its delivery note: keeps working',
        informeCierre: 'Left its closing report: leaving',
        penso: 'Thought for {segundos} s',
        error: 'Had a technical fault. If it happens again, tell me.',
        esperaConfirmacion: 'Waiting for a confirmation they cannot ask you for here. Tell me.',
      },
    },
    accion: {
      siHazlo: 'Yes, do it',
      plan: 'Before starting, it explains how it will do it',
      planPreguntasUna: 'And asks you 1 question.',
      planPreguntasVarias: 'And asks you {n} questions.',
      revisarPlan: 'Read and give the go-ahead',
      bloquea: 'Cannot continue without your answer.',
      noBloquea: 'Keeps working meanwhile.',
      recomendada: 'What they recommend',
      porQueRecomienda: 'Why they recommend it:',
      tuRespuesta: 'Your answer',
      responder: 'Answer',
      pidePermiso: 'Asks your permission to:',
      verDetalles: 'See details',
      ocultarDetalles: 'Hide details',
      recomendado: 'recommended',
      oEscribele: 'or write to them',
      porQue: 'Why:',
      riesgo: 'What could go wrong:',
      deshacer: 'How to undo it:',
      permisoPronto: 'Approving this from here is coming soon; meanwhile they will not do it on their own.',
      noLoHagas: "No, don't do it",
      integrar: 'Finished. Is the work good?',
      revisorOk: 'Another agent reviewed it and it looks good:',
      revisorReparos: 'Another agent reviewed it and it looks good, with caveats:',
      revisorNo: "Another agent reviewed it and wouldn't accept it:",
      siIntegrar: 'Yes, accept it',
      noIntegrar: 'No',
      atasco: "Couldn't finish",
      seguir: 'Keep working',
      forzar: 'Finish without keeping its work',
      forzarConfirma: 'It will leave without its work entering the project. Whatever it did is kept aside and not lost.',
      forzarSi: 'Yes, finish like this',
      cancelar: 'Cancel',
      bloqueado: 'Waiting on you to continue',
      sinEntregar: 'Some messages did not reach them',
      sinEntregarEncargo: 'The initial brief',
      sinEntregarOrden: 'One of your answers',
      reenviar: 'Resend',
      yaNoAplica: 'no longer applies',
    },
    plan: {
      titulo: 'How it will do it',
      como: 'How it will do it',
      entiendo: 'What they understood',
      preguntaUna: 'They ask you',
      preguntasVarias: 'They ask you ({n})',
      soloMira: 'It will only look and report: changes nothing',
      puesto: 'Role they chose: {puesto}',
      decideTu: 'You decide',
      siNoContestas: '— if you do not answer: {texto}',
      veoMal: 'What they see wrong',
      propone: 'Proposes:',
      cambialo: "You're right, change it",
      dejalo: 'Leave it as is',
      veoMalAyuda: 'If you mark nothing on a point, they will not touch it without asking.',
      objetivos: 'Done when',
      objetivosInvestigar: 'Questions they will answer',
      pasos: 'Steps',
      supuestos: 'They assume that',
      entregables: 'What they will hand over',
      faltaUna: '1 answer missing',
      faltanVarias: '{n} answers missing',
      pedirCambios: 'Ask for changes',
      correccion: 'What do you want changed?',
      enviarCambios: 'Send',
      aprobar: 'Give the go-ahead',
    },
    informe: {
      verDatos: 'See the data',
      grafica: 'Chart',
      recomendada: 'Recommended',
      imagen: 'Image',
      imagenNoDisponible: 'Image not available',
      recomendacion: 'My recommendation',
      decision: 'What you need to decide',
      riesgo: 'Risk',
      bien: 'Already done',
      nota: 'Note',
    },
    archivo: {
      informe: 'Report',
      documento: 'Document',
      paginas: '{n} pages',
      unaPagina: '1 page',
      abrir: 'Open “{titulo}”, {detalle}',
    },
    vistaPrevia: {
      cerrar: 'Close (Esc)',
      descargar: 'Download',
      abrirFuera: 'Open in a new tab',
      acercar: 'Zoom in (+)',
      alejar: 'Zoom out (−)',
      ajustar: 'Fit',
      ajustarPista: 'Fit to width (0)',
      zoom: 'Size',
      paginas: 'Pages',
      carril: 'Document pages',
      imagenes: 'Images in this message',
      pagina: 'Page {n}',
      paginaDe: '{n} of {total}',
      anterior: 'Previous (←)',
      siguiente: 'Next (→)',
      abriendo: 'Opening…',
      error: "Couldn't open it. Try again or open it in a new tab.",
      hoy: 'today, {hora}',
      ver: 'View {nombre} full size',
    },
    fijar: {
      poner: 'Pin to top',
      quitar: 'Unpin',
      error: "Couldn't pin it. Try again.",
    },
    info: {
      volverAlChat: 'Back to the conversation',
      titulo: 'Info',
      renombrar: 'Rename',
      nombre: 'Agent name',
      guardarNombre: 'Save',
      nombreLargo: 'Between {min} and {max} characters',
      ver: 'Show info',
      ocultar: 'Hide info',
      deQueVa: 'What this is about',
      ahora: 'Right now',
      pensando: 'Thinking:',
      siguiente: 'Next:',
      actualizado: 'Updated at {hora}',
      sinNoticias: 'No news for a while',
      progreso: 'Progress',
      objetivos: '{hechos} of {total} goals',
      sinPlan: "It hasn't explained how it will do it yet.",
      criterio: {
        cumplido: 'Done',
        en_curso: 'In progress',
        pendiente: 'Pending',
        fallando: 'Failing',
        aparcado: 'Parked',
      },
      riesgos: 'Risks they see',
      sinComprobar: 'Not checked yet',
      notaEntrega: 'Its latest delivery note',
      integrado: 'Its work was saved into the project with your go-ahead',
      sinIntegrar: 'Finished without saving its work into the project (kept aside)',
      noHecho: 'Not done:',
      noHechoLinea: '{que} — {por_que}',
      siContinuas: 'If someone continues:',
      gasto: 'Spent:',
      gastoLinea: '${dolares} — {en_que}',
      gastoSolo: '${dolares}',
      verPropuesta: 'See the proposed changes',
      encargo: 'Brief',
      paraQue: 'What for:',
      estaraBien: 'It will be right if:',
      tareas: 'Their tasks',
      detalles: 'Details',
      historial: 'History ({n})',
      boton: 'Info · {atajo}',
      ficha: {
        noNecesita: 'It does not need you now',
        cerrando: 'Nothing: the office is reviewing its work',
        problema: 'Nothing: it has a technical problem and the office is fixing it',
        loPedisteTu: 'you asked for it',
        loPidio: '{nombre} asked for it',
        loPusoLaOficina: 'the office set it up (cleanup)',
        paraContratoSanti: 'for the contract with Santi',
        hoy: 'today',
        ayer: 'yesterday',
        elDia: 'on {dia}',
        paraQue: 'What for',
        tusPalabras: 'Your words:',
        entreComillas: '“{texto}”',
        dondeVa: 'Where it is',
        empezando: 'Starting · reading your brief',
        arreglando: 'The office is fixing it',
        vuelveHoy: 'Checks again at {hora}',
        vuelveManana: 'Checks again tomorrow at {hora}',
        vuelveDia: 'Checks again on {dia} at {hora}',
        despues: 'Next:',
        resultado: 'Result',
        conSanti: 'With Santi',
        leToca: 'With Santi · his turn since {hace}',
        teToca: 'With Santi · your turn since {hace}',
        yMas: 'and {n} more',
        archivosVacio: 'Photos and files from the chat will show up here',
        potencia: '{potencia} effort',
        alDia: 'Up to date · {hace}',
        resumenDe: 'Summary from {hace}',
      },
    },
    cierre: {
      boton: 'Finish this work',
      titulo: 'Shall we finish this work?',
      progreso: 'Progress: {hechos} of {total} goals met.',
      paso1: "Finishes or parks what's pending, checks everything works, saves its work and leaves a note for whoever continues.",
      paso2: 'Another agent reviews the work against what you agreed.',
      paso3: "Asks you to accept its work, with the reviewer's opinion in a few words.",
      paso3Investigar: "Asks you to accept its report, with the reviewer's opinion in a few words.",
      tarda: 'It usually takes 3 to 10 minutes.',
      avisoPregunta: 'They asked you «{pregunta}» and you have not answered. If you close, they will decide and note it down.',
      avisoTrabajando: 'They are working: the wrap-up will start when they finish what they are doing.',
      cerrarBien: 'Finish properly',
      todaviaNo: 'Not yet',
      despedir: 'Dismiss without tidying up',
      despedirConfirma: 'Sure? They will leave without saving or reviewing anything. Anything unsaved will be lost.',
      despedirSi: 'Yes, dismiss',
      integrando: 'Merging their work, without leaving: {paso}',
      paso: {
        esperando_informe: 'Finishing: tidying up and writing its closing report',
        comprobando: 'Finishing: the office is checking its work',
        revisando: 'Finishing: another agent is reviewing its work',
        esperando_permiso: 'Finishing: waiting for you to accept its work',
        publicando: 'Finishing: saving its work into the project',
        en_cola: 'Finishing: queued to save its work',
        archivando: 'Finishing: clearing its desk',
      },
    },
    voz: {
      nota: 'Record a voice note',
      grabando: 'Recording a voice note',
      escCancela: 'Esc to cancel',
      casiMaximo: 'Almost at the limit (3 minutes max)',
      cancelar: 'Cancel the note',
      enviar: 'Send the note',
      lista: 'Voice note ready ({duracion})',
      descartar: 'Discard the note',
      reintentar: 'Retry',
      sinPermiso: 'Allow the microphone for this site in the browser bar.',
      noSoportado: 'This browser cannot record voice notes.',
    },
    contratarVentana: {
      adjuntar: 'Attach screenshots or files (you can also paste them here)',
      adjuntarMas: 'Add more',
      quitarAdjunto: 'Remove {nombre}',
      adjuntosMensaje: 'Here are the screenshots and files I mentioned in the request.',
      adjuntosFallo: 'Could not send {n} of the attached files: send it in its chat.',
      titulo: 'Hire someone',
      explica: 'Tell them what you need as you would tell a person. They will read it, look at the project and explain how they will do it before touching anything.',
      placeholder: 'For example: «Maddison’s fans say the bot answers in English. Find out why and how often, without changing anything.»',
      atajo: 'Ctrl + Enter to hire',
      necesita: 'What they will need to know',
      resultado: 'What do you want to achieve?',
      paraQue: 'What do you need it for? What triggered it?',
      comoSabre: 'How will you know it is done right?',
      noLoSe: 'I do not know: help me define it',
      faltaMaterial: 'You mention something you have not pasted: paste it here so they see it as is.',
      secreto: 'This looks like a key. In a chat it is stored forever: remove it before hiring.',
      quien: 'Who does it',
      sala: 'Room',
      modelo: 'Model',
      potencia: 'Power',
      potenciaAyuda: 'More power thinks more at each step: better for hard things, slower for simple ones.',
      contexto: 'Add context',
      contextoAyuda: 'Optional: what for, how you will know it is right, what not to touch… If missing, they will ask in their plan.',
      noTocar: 'What must they NOT touch?',
      nombre: 'Agent name',
      nombreAyuda: 'Leave it empty and one is picked.',
      tema: 'Title (optional)',
      temaAyuda: "What you'll see in the office to recognise it. Leave it empty and reception writes one from your request.",
      contratar: 'Hire {nombre}',
      contratarSinNombre: 'Hire',
      descartar: 'Discard what you wrote?',
      llegado: 'Arrived at {sala}: “{nombre}”',
      ver: 'View',
      sinSalas: 'There is no room enabled yet.',
    },
    // 04-11 — notifications on this device (ActivarAvisos.tsx): turn them on with one click from the office.
    avisosAparato: {
      aparato: { ordenador: 'this computer', ipad: 'this iPad', iphone: 'this iPhone' },
      pedir: 'Turn on notifications on {aparato} to know when Santi writes to you',
      activar: 'Turn on',
      activados: 'Notifications are on for {aparato}.',
      fallo: 'Notifications could not be turned on for {aparato}. Try again.',
      bloqueadosNavegador: 'Notifications are blocked in this browser. To allow them, click the icon left of the address, turn on "Notifications" and reload the page.',
      bloqueadosApp: 'Notifications are blocked on {aparato}. To allow them: Settings › Notifications › Venuz · Oficina › "Allow Notifications".',
      instalar: 'To get notifications on {aparato}, add the office to the Home Screen (Share › "Add to Home Screen") and open it from there.',
      quitar: 'Dismiss',
      // 04-11 — "Notifications and sound" (avisos/HojaAvisos.tsx) and the notice inside the open office (avisos/AvisosDeSanti.tsx).
      avisosYSonido: 'Notifications and sound',
      sonido: {
        titulo: "Sound for Santi's notifications",
        probar: 'Play',
        volumen: 'Volume',
        volumenEtiqueta: "Volume of Santi's notifications",
        porcentaje: '{n}%',
        nombres: { campanita: 'Little bell', gota: 'Drop', doble: 'Double tone', suave: 'Soft', ninguno: 'No sound' },
      },
      hoja: {
        descripcion: 'With the office open, when Santi writes to you a notice appears at the bottom right and the sound you choose plays.',
        aparato: 'Notifications on {aparato}',
        activos: 'On for {aparato}.',
        pedir: 'Off for {aparato}.',
        sinSoporte: 'This browser cannot show system notifications.',
        sinServidor: 'The office cannot send notifications to devices yet.',
        sistema: {
          windows: "The Windows notification plays the Windows sound. If it doesn't arrive, in Windows: Settings › System › Notifications, and allow your browser.",
          mac: "The Mac notification plays the Mac sound. If it doesn't arrive, on the Mac: System Settings › Notifications, and allow your browser.",
          ipad: 'The iPad notification plays the iPad sound: change it in Settings › Notifications › Venuz · Oficina.',
          iphone: 'The iPhone notification plays the iPhone sound: change it in Settings › Notifications › Venuz · Oficina.',
          otro: 'The system notification plays the system sound: that cannot be chosen from here.',
        },
      },
      aviso: { abrir: '{titulo}: {texto}. Open the conversation' },
    },
    // 01-11 — contracts with Santi. 03-11: the new «Santi» window (docs/pelotas/ux-2026-11-03/propuesta/propuesta.md;
    // no ball, no file card, no "Cancel all", no "General": "Chat with Santi").
    pelotas: {
      pau: 'Pau',
      santi: 'Santi',
      tu: 'You',
      loEnviaPau: 'Only Pau sends what reaches Santi.',
      contador: '{n}/{max}',
      pantalla: {
        titulo: 'Santi',
        nadaNuevo: 'Nothing new',
        nuevo: 'new',
        nuevos: 'new',
        yMas: '{titulo} and {n} more',
        vencio: 'Overdue',
        venceHoy: 'Due today',
        sinConexion: 'Offline',
        sinEnviar: 'Not sent',
        abrir: 'Open the Santi window',
      },
      lista: {
        titulo: 'Contracts with Santi',
        pedirAlgo: 'Ask Santi for something',
        chat: 'Chat with Santi',
        deQuien: '{quien}: {texto}',
        teToca: 'Your turn',
        esperandoA: 'Waiting for {quien}',
        hechoSemana: 'Done this week',
        hechoPor: 'Done by {quien}',
        canceladoPor: 'Cancelled by {quien}',
        ti: 'you',
        vacioTitulo: 'Nothing pending with Santi',
        vacioTexto: 'When Santi asks you for something, it will show up here.',
        sinConexion: 'No connection with Santi right now',
        sinConexionDesde: 'No connection with Santi right now · what you see is from {hace}',
        nuevo: 'New',
        cargando: 'Loading the contracts with Santi',
      },
      fecha: {
        hoy: 'today',
        ayer: 'yesterday',
        el: 'on {fecha}',
        venceHoy: 'Due today',
        venceManana: 'Due tomorrow',
        venceEl: 'Due {dia}',
        vencioEl: 'Was due {dia}',
        hoyCorto: 'Today',
        mananaCorto: 'Tomorrow',
        elCorto: '{dia}',
        vencioCorto: 'Overdue',
        paraHoy: 'For today',
        paraManana: 'For tomorrow',
        paraEl: 'For {dia}',
        teLoPidio: '{quien} asked you {cuando}',
        seLoPediste: 'You asked {cuando}',
      },
      ventana: {
        titulo: 'Santi',
        cerrar: 'Close',
        verLista: 'Contracts with Santi',
        teToca: 'Your turn:',
        esperandoA: 'Waiting for {quien}:',
        chatSub: 'Day-to-day things. Anything to follow up, note it as a contract.',
        hecho: 'Done',
        cancelar: 'Cancel',
        cancelado: 'Cancelled',
        masAcciones: 'More actions',
        contratarAgente: 'Hire an agent for this',
        verAgente: 'See the agent working on it',
        cargando: 'Opening the conversation…',
        sinMensajes: 'Nothing written here yet.',
        nuevo: 'New',
        hechoEl: 'Done on {fecha}.',
        canceladoEl: 'Cancelled on {fecha}.',
      },
      chat: {
        escribe: 'Write to Santi…',
        enviar: 'Send',
        pistaLeToca: 'When you send, it’s Santi’s turn',
        pistaSigues: 'When you send, it’s still your turn',
        pistaLeTocaCorta: 'Santi’s turn',
        pistaSiguesCorta: 'Still your turn',
        noSigoYo: 'No, I’ll keep it',
        queLeToque: 'Make it Santi’s turn',
        noSePudo: 'Could not send it. Try again.',
        textoLargo: 'The message is too long: {max} characters at most.',
      },
      pedir: {
        titulo: 'Ask Santi for something',
        sub: 'He gets it from you. You’ll see it under “Waiting for Santi”.',
        vacioTitulo: 'Something new with Santi',
        vacioTexto: 'Write it the way you would tell him. Once sent, you’ll keep talking about it right here.',
        campoTitulo: 'Title (leave it empty and your agent writes it)',
        campoTexto: 'What do you need from Santi?',
        paraCuando: 'By when',
        sinFecha: 'No date',
        manana: 'Tomorrow',
        elViernes: 'Friday',
        otroDia: 'Another day…',
        descartar: 'Discard',
        tituloLargo: 'The title is too long: {max} characters at most.',
        textoLargo: 'What you need from Santi is too long: {max} characters at most.',
        datosInvalidos: 'Santi can’t receive it like this: the title, {titulo} characters at most, and what you need, {texto} at most.',
      },
      acto: {
        hecho: '“{titulo}” done. We’ll tell Santi.',
        cancelado: '“{titulo}” cancelled. We’ll tell Santi.',
        deshacer: 'Undo',
        decirPorque: 'Tell him why',
        porque: 'Why? Santi will see it',
        enviar: 'Send',
        falloHecho: 'Could not mark “{titulo}” as done. Try again.',
        falloCancelar: 'Could not cancel “{titulo}”. Try again.',
        volverAProbar: 'Try again',
        quitarAviso: 'Remove the notice',
        entreComillas: '“{titulo}”',
      },
      // Delivery 3 (04-11 night): THAT thread's agent, beside (desktop) or over (iPad) — AgenteDelHilo.
      agente: {
        titulo: 'Your agent',
        dePau: 'Pau’s agent',
        subContrato: 'Handles this contract. Santi doesn’t see this.',
        subGeneral: 'For the chat with Santi. Santi doesn’t see this.',
        subPedir: 'Helps you ask for it. Santi doesn’t see this.',
        cerrar: 'Close your agent',
        escribe: 'Tell your agent something…',
        poniendo: 'Assigning an agent…',
        noSePuso: 'Could not assign an agent.',
        sinAgente: 'No agent yet: the office assigns one when Pau opens it.',
        cargando: 'Opening the chat…',
      },
      // Delivery 3: what the agent proposes, in ITS chat (PropuestaEnElChat); it only reaches the box with "Use in the box".
      propuesta: {
        titulo: 'Proposal for Santi',
        tituloCerrar: 'Proposal for Santi · mark it done',
        usar: 'Use in the box',
        cambiar: 'Change',
        fila: 'Proposal ·',
      },
      borrador: {
        etiqueta: 'Your agent’s draft',
        descartar: 'Discard',
        preparar: 'Let your agent prepare it',
        preparando: 'Preparing…',
        hayUno: 'Your agent has a reply ready',
        verla: 'See it',
        puesto: 'Your agent’s reply is in the box',
        deshacer: 'Undo',
        noSePudo: 'Could not ask your agent. Try again.',
        sinRespuesta: 'Your agent hasn’t left anything yet. Try again in a while.',
        fila: 'Draft ·',
        pedidoListo: 'I’ve prepared a request for Santi',
        revisar: 'Review',
      },
    },
    despliegues: {
      filaContrasena: 'Set your Allow password',
      filaContrasenaExplica: 'Without it you cannot approve or send back deployments from here. It takes a moment.',
      pedidoDesplegar: 'Requested to deploy',
      creoQueLoPidio: 'I think it was requested by:',
      vuelveTrasNota: 'Back after your note: “{nota}”',
      masInfoNoDisponible: 'There is no long explanation for this work. Ask whoever requested it or ask for a second opinion.',
      rechazarSinMas: 'Just reject',
      allowIgualmente: 'Allow anyway',
      sinContestar: 'It has not answered “{pregunta}” yet. If you Allow now, you do it without its answer.',
      esperaDesde: 'Waiting for your Allow since {hace}',
      loPidio: 'Requested by:',
      noSeQuien: "I don't know which agent requested it",
      yaNoEsta: 'no longer in the panel',
      hablarCon: 'Talk to it',
      vistaResumen: 'Summary',
      vistaInfo: 'More information',
      vistaHablar: 'Talk',
      vistas: 'What to show',
      puntoPorPunto: 'What changes, point by point',
      porQue: 'Why it is done',
      comoSeComprobo: 'How it was checked',
      quePodriaSalirMal: 'What could go wrong',
      queToca: 'Parts of Calypso it touches',
      masInfoPreparando: 'Preparing the full explanation…',
      masInfoFuente: "Explanation written by AI from the agent's proposal and the actual changes. If something doesn't add up, ask it.",
      segundaOpinion: 'Ask for a second opinion',
      segundaOpinionExplica: "Want another agent to review it in depth and discuss it with you? It will appear in the office and you'll talk through its chat.",
      segundaOpinionEncargo: 'A deployment is waiting for my Allow: “{que}”. {cambia} Review it in depth before I approve it: what exactly changes, whether it makes sense, what risks it has and whether it was checked properly. Explain it without jargon and answer my questions. Do not change or approve anything: only review and give your opinion. (For you: the deployment is {enlace}.)',
      hablarExplica: 'What you write reaches whoever requested it, with the context of this deployment. It replies here.',
      hablarVacio: 'Ask anything about this change.',
      hablarEscribe: 'Write your question…',
      hablarNoEsta: 'Whoever requested it is no longer in the panel, so it cannot answer. You can ask another agent to review it with you.',
      hablarNoSe: "I don't know which agent requested it, so I can't pass on your question. You can ask another agent to review it with you.",
      devolver: "Something doesn't convince me",
      devolverBoton: 'Send back to adjust',
      devolverTitulo: "What don't you like?",
      devolverExplica: 'Write what you want changed. I tell “{quien}” your note, what touches the fans leaves the official version and it will ask you again, adjusted. If you do not want it done, “Just reject”.',
      devolverExplicaSinQuien: 'Whoever requested it is not available, so your note is kept in the log. If you do not want it done, “Just reject”.',
      devolverNota: 'For example: “I do not want it to affect all accounts at once; start with one.”',
      devuelto: 'Sent back to “{quien}”: it will ask you again with your note',
      loLanzo: 'Requested by an agent {hace}',
      queCambia: 'What changes',
      queApruebas: 'What you approve',
      siSaleMal: 'If it goes wrong',
      siRechazas: 'If you reject it',
      queSubeUno: 'What goes up: 1 change',
      queSubeVarios: 'What goes up: {n} changes, from every agent',
      ningunoFans: 'None of them touches what answers the fans.',
      restoUno: '1 more change that does not touch the fans',
      restoVarios: '{n} more changes that do not touch the fans',
      yMas: 'And {n} more that do not fit here: ask the agent before you Allow.',
      sinResumenCambio: 'No summary from the agent: it is what the title says.',
      verCambio: 'See the change on GitHub',
      vuelveRechazado: 'Brings back something you rejected before',
      esRetirada: 'Takes out something you rejected (rejecting the deploy does not undo this)',
      filaNingunoFans: 'Nothing that goes up touches the fans',
      filaFansUno: '1 change touches the fans: {quienes}',
      filaFansVarios: '{n} changes touch the fans: {quienes}',
      preparando: 'Preparing the explanation…',
      sinResumen: "I couldn't summarise it. If you don't know what it is, ask the agent before you Allow.",
      detallesTecnicos: 'Technical details (for the agents)',
      tituloUno: '1 release awaits your Allow',
      tituloVarios: '{n} releases await your Allow',
      conAviso: '{n} with a warning',
      soloLectura: 'Diagnostic: read-only, changes nothing',
      soloLecturaExplica: 'This is a read-only diagnostic: it changes nothing in production. It asks for your Allow because it still shares the production environment with deployments.',
      muchosDiagnosticos: 'These are read-only diagnostics: once they move to their own environment they will stop asking for your Allow.',
      version: 'Version',
      propuesta: 'pull request #{n}',
      lanzadoPor: 'Started by',
      cuando: 'When',
      cambiosEnv: 'Settings (.env) changes it carries',
      sinCambiosEnv: 'None: production settings stay as they are.',
      comoSeDeshace: 'How to undo it',
      verEnGithub: 'See on GitHub',
      tuContrasena: 'Your Allow password',
      tuCodigo: 'Your Google Authenticator code',
      allow: 'Allow',
      rechazar: 'Reject',
      aprobado: 'Allowed: «{workflow}» goes ahead',
      rechazado: 'Rejected: «{workflow}» will not run',
      bloqueado: 'Too many wrong passwords: locked until {hora}. I have warned you on Telegram.',
      sinContrasena: 'You have no Allow password yet. Set it once and you can approve from here.',
      fijarBoton: 'Set my Allow password',
      fijarTitulo: 'Your Allow password',
      fijarExplica: 'You set it once, with the panel password. It is stored encrypted, I will ask for it on every Allow and alert you on Telegram whenever it is set or used. Note: while agents share your GitHub account, this password is your gate and your alarm, not a lock against them.',
      contrasenaPanel: 'Panel password',
      nueva: 'New Allow password',
      nuevaAyuda: 'At least 10 characters and different from the panel password.',
      repite: 'Repeat it',
      noCoinciden: 'They do not match.',
      fijarGuardar: 'Save',
      fijada: 'Allow password saved',
      rama: 'Branch',
      vueltaAtras: 'Rolls back to version {sha}',
      versionDesconocida: 'unknown',
      cambiosEnvDesconocido: "I couldn't read them. Don't Allow without checking on GitHub.",
      copiasNginx: 'Copies and nginx',
      copiasNoSeVen: 'Not visible before the Allow (default: all three copies, nginx untouched).',
      copiasSi: '{copias} · {nginx}',
      nginxSi: 'also changes nginx',
      nginxNo: 'nginx untouched',
      incompleta: "I couldn't read everything on GitHub: there may be more waiting.",
      sinTitulo: 'untitled',
      lineas: {
        aria: 'What this upload changes',
        cambiaUna: '1 thing changes in production',
        cambiaVarias: '{n} things change in production',
        ninguna: 'Nothing in this upload changes what fans experience.',
        saleCon: 'Not going up: it is in the same work as «{titulo}»',
        noSube: 'Not going up with this Allow',
        deshacer: 'Undo',
        antes: 'Before',
        ahora: 'Now',
        nuevo: 'New',
        calypsoLeera: 'Calypso will read',
        pocoSegura: 'This reading is not fully certain: ask if it matters.',
        preguntarA: 'Ask {quien}',
        preguntar: 'Ask',
        quitar: 'Remove',
        aQuien: 'Who it affects:',
        noHablado: 'You did not discuss this with {quien}',
        noHabladoSinQuien: 'You did not discuss this with the agent',
        rechazado: 'You said no:',
        lePediste: 'You asked for it:',
        quitarTitulo: 'If you remove it, this leaves the upload:',
        trabajoDe: '«{titulo}», by {agente}',
        unAjuste: 'The setting on this line (the code does not change).',
        variosAjustes: 'The {n} settings on this line (the code does not change).',
        arrastraUna: 'It is in the same work, so this leaves too:',
        arrastraVarias: 'They are in the same work, so these leave too:',
        quitarExplica: 'The rest goes up with this same Allow: the agent relaunches it without this and, if nothing new slips in, it goes up without asking you again. I will tell you on Telegram.',
        quitarConfirmar: 'Remove this line',
        quitarConArrastre: 'Remove these {n} lines',
        hablasCon: 'You are talking to {quien} about this line. The upload waits meanwhile.',
        hablasConQuienPidio: 'You are talking to whoever asked for the upload, about this line. The upload waits meanwhile.',
        preguntaPlaceholder: 'Ask or request a change…',
        preguntaAria: 'Question about «{titulo}»',
        sinEfectoUno: 'And 1 piece of work that changes nothing for fans',
        sinEfectoVarios: 'And {n} pieces of work that change nothing for fans',
        aprobadoQuitando: 'Allow given removing {n}: the rest goes up as soon as it comes back without it. I will tell you on Telegram.',
        allowParcial: 'Allow {n} · remove {m}',
        loPide: '{quien} asks for it',
        creoQueLoPide: 'I think {quien} asks for it',
        volver: 'Back to the upload',
        trasQuitar: 'What you removed leaves the official version. The rest goes up with this Allow as soon as it comes back without it; I will tell you on Telegram.',
        preparando: 'Reading exactly what changes, line by line. It takes 1-3 minutes; meanwhile, here is the usual summary.',
        noDisponible: 'I could not prepare the line-by-line detail for this upload. Below is the usual summary; if in doubt, ask before the Allow.',
        masDetalles: 'If you reject it, if it goes wrong, and technical details',
        verExplicacion: 'Long explanation',
        hablarSubida: 'Talk to {quien} about the whole upload',
        cita: '«{texto}»',
        filaSinHablarUna: '1 you did not discuss',
        filaSinHablarVarias: '{n} you did not discuss',
      },
    },
    s12: {
      situacion: {
        llegando: 'Starting',
        autorizacion: 'Asks for your permission',
        bloqueado: "Can't continue without you",
        te_escribio: 'Waiting for your reply',
        problema: 'Has a technical problem',
        trabajando: 'Working',
        cerrando: 'Closing',
        sin_pendiente: 'Nothing pending',
        saliendo: 'Leaving',
      },
      conSanti: { marca: 'Has a contract with Santi' },
      // Solo queda «Se van» (mundo/Salida.tsx); los grupos de la lista están en `lista.bloque` (Entrega 3).
      grupo: {
        se_van: 'Leaving',
      },
      fila: {
        permisoSinTexto: 'Asks permission to continue',
        bloqueadoSinTexto: 'Needs something from you to continue',
        teEscribioSinTexto: 'Left you a message',
        terminado: 'Finished: {que}',
        terminadoSinTexto: 'handing over its work',
        problemaSinTexto: 'Something technical is stopping them',
        problemaOficina: '{que}. The office is fixing it.',
        trabajandoSinTexto: 'Working on your request',
        cerrandoSinTexto: 'The office is checking their work',
        integrando: 'Merging their work, without leaving: {paso}',
        llegando: 'Reading your request',
        saliendo: 'Clearing their desk',
        sinPendienteSinTexto: 'Nothing pending',
      },
      cierre: {
        esperando_informe: 'Writing the closing report',
        esperando_nota: 'Writing its delivery note',
        comprobando: 'Checking everything works',
        revisando: 'Another agent is reviewing the work',
        esperando_permiso: 'Waiting for you to accept the work',
        publicando: 'Merging the work',
        en_cola: 'Queued to merge the work',
        archivando: 'Clearing their desk',
      },
      resumen: {
        permisoUno: '1 permission',
        permisoVarios: '{n} permissions',
        bloqueadoUno: "1 can't continue",
        bloqueadoVarios: "{n} can't continue",
        problemaPauUno: '1 problem for you to fix',
        problemaPauVarios: '{n} problems for you to fix',
        teEscribioUno: '1 waiting for your reply',
        teEscribioVarios: '{n} waiting for your reply',
        trabajando: '{n} working',
        cerrando: '{n} closing',
        problemaUno: '1 with a problem',
        problemaVarios: '{n} with a problem',
        nadaTeToca: 'Nothing for you right now',
      },
      sinLeer: 'Unread',
      rapido: {
        si: 'Yes',
        aria: 'Yes, allow {nombre}: {que}',
        pista: 'Allow without opening the chat',
        error: 'Could not allow {nombre}: {motivo}',
      },
      teEscribioTerminados: { uno: 'Waiting for your reply (1 has finished)', varios: 'Waiting for your reply ({n} have finished)' },
      sinNoticias: 'no news · {cuanto}',
      duracion: { min: '{n} min', h: '{n} h', d: '{n} d' },
      esperaDesde: 'Waiting since {hace}',
      bandejas: {
        esperan: '{n} waiting for {nombre}',
        selector: 'Whose inbox',
        mia: 'Mine',
        mirando: "You're looking at {nombre}'s inbox",
        volver: 'Back to mine',
        de: "{nombre}'s",
      },
      franja: {
        permiso: 'Asks permission: {que}',
        permisoCargando: 'Loading what they ask…',
        deshacer: 'How to undo: {que}',
        detalles: 'Details',
        ocultarDetalles: 'Hide details',
        bloqueado: "Can't continue: {que}",
        bloqueadoAyuda: 'Reply below: as soon as you write, they continue.',
        responderRapido: 'Quick replies',
      },
      maqueta: {
        boton: 'S12 mockup: {escenario} ▾',
        vacia: 'Empty',
        'uno-de-cada': 'One of each state',
        llena: 'Full office',
        'nada-te-toca': 'Nothing for you',
        'chat-autorizacion': 'Chat: asks permission',
        'chat-bloqueado': "Chat: can't continue",
        'chat-te-escribio': 'Chat: wrote to you',
      },
    },
    maqueta: {
      escenarios: 'Mockup scenarios',
      boton: 'Mockup: {escenario} ▾',
      tres: '3 agents',
      uno: '1 agent',
      hover: 'On hover',
      despliegue: 'Pending deployment',
      vacia: 'Empty',
      llena: '12 agents',
      chat: 'Chat',
      plan: 'Plan',
      contratar: 'Hire',
    },
    errores: {
      SALA_PRINCIPAL: 'The main room cannot be removed.',
      SALA_OCUPADA: 'That room has agents working: close their work first.',
      SALA_DESCONOCIDA: 'That room no longer exists.',
      AUN_SE_REINTENTA: 'That message is still being retried automatically. Wait a little.',
      AGENTE_CERRANDO: 'The office is checking or merging their work: you cannot write to them now (you can once it is done).',
      PERMISO_NO_VALIDO: 'That permission is no longer valid for merging.',
      NO_SE_PUEDE_CERRAR: 'That agent is not working now.',
      NO_ESTA_ATASCADO: 'That wrap-up is no longer stuck.',
      NO_SE_PUEDE: 'That cannot be done with this agent right now.',
      PERMISO_YA_RESPONDIDO: 'That permission already has an answer.',
      PERMISO_AUN_NO: 'This cannot be approved from the office yet.',
      RUNNER_OFFLINE: 'The server they work on is not responding right now. Try again in a moment.',
      CONTRATADO_CERRADO: 'This agent already finished their work.',
      FORMULARIO_INVALIDO: 'Something is missing or too long in the brief. Check the fields.',
      DATOS_INVALIDOS: 'Something you sent does not fit. Try again.',
      PROYECTO_NO_HABILITADO: 'That room is no longer available.',
      MESA_YA_PUESTA: 'There is already a desk there.',
      TOPE_MESAS: 'That room already has as many desks as fit.',
      MESA_DESCONOCIDA: 'There is no desk there any more. Look at the office again.',
      GRUPO_NOMBRE_REPETIDO: 'There is already a group with that name in that room.',
      GRUPO_DESCONOCIDO: 'That group no longer exists. Look at the office again.',
      GRUPO_NO_VALIDO: 'A group name must be 1 to 24 letters long.',
      OTRA_SALA: 'Each room is its own project: an agent never changes room.',
      MESA_OCUPADA: 'Someone is sitting at that desk: move them first.',
      ULTIMA_MESA: 'A room keeps at least one desk.',
      AGENTE_DESCONOCIDO: 'That agent is no longer in that room.',
      SALA_LLENA: 'That room has no free desks. Close some work or pick another room.',
      SIN_RUNNER_CAPAZ: 'The servers are updating. Try again in a few minutes.',
      CAPACITY_UNKNOWN: 'The servers are starting up. Try again in a minute.',
      NOMBRE_OCUPADO: 'There is (or recently was) an agent with that name. Pick another.',
      ZONA_OCUPADA: 'Another agent is already working on that: message it to extend its task instead.',
      PARECE_UNA_CLAVE: 'The brief contains something that looks like a key. Remove it before hiring.',
      CLAVE_USADA: 'That brief was already sent. Press again to hire anew.',
      SIN_CONTRATO: 'Has not proposed a plan yet.',
      VERSION_DESFASADA: 'Their plan changed while you were looking. Review the new one.',
      CONTRATO_YA_RESPONDIDO: 'That already has an answer.',
      RESPUESTAS_INCOMPLETAS: 'Answer all their questions or tick «You decide».',
      PREGUNTA_YA_RESPONDIDA: 'That question is already answered.',
      NO_ES_DE_LA_OFICINA: 'That agent is not from the office.',
      // 01-11 — contracts with Santi (pelotas).
      SOLO_PAU: 'Writing to Santi is for Pau, from his personal session: sign in with your user and the Authenticator code.',
      SIN_LLAVE: 'The office has no key for the contracts board with Santi yet: an agent has to install it.',
      LLAVE_CORTADA: 'The office key to the contracts board was cut for safety: an agent has to put a new one.',
      PARECE_UN_SECRETO: 'The text looks like it contains a key or password. Remove it: what is sent to Santi cannot be deleted.',
      NO_TIENES_LA_PELOTA: 'It’s Santi’s turn now: check the conversation and, if it’s still needed, send it again.',
      TEMA_CERRADO: 'That contract is already done.',
      IDEMPOTENCIA_REUTILIZADA: 'The previous one may already have arrived: check the conversation before sending it again.',
      NO_DISPONIBLE: 'The contracts board with Santi does not answer right now. Try again in a moment.',
      TIEMPO: 'The contracts board took too long to answer. Try again.',
      RED: 'Could not reach the contracts board. Try again.',
      YA_RESUELTA: 'That card was already answered.',
      NO_ENCONTRADO: 'That contract or card no longer exists.',
      FORBIDDEN: 'Only Pau can do this.',
      PROPUESTA_NO_PENDIENTE: 'That proposal is no longer pending: the agent will propose again.',
      // 02-11 — in the «Santi» window these centre codes mean something else (or had no text).
      pelotas: {
        VERSION_DESFASADA: 'The contract changed while you were looking (Santi moved something). Check the conversation and send it again.',
        LIMITE: 'Too many sends to the board in a row. Wait a moment and try again.',
        NO_ES_TUYO: 'Santi asked for this one: until the board has the new rule, only he can close it. Tell him in the conversation.',
        TABLERO_SIN_CANCELAR: 'The contracts board with Santi can’t cancel yet (it’s being updated). Try again in a while or mark it as “Done”.',
        PROPUESTA_SUSTITUIDA: 'Your agent has proposed something else since: use the latest one.',
        SIN_PROPUESTA: 'That proposal is no longer there.',
        DEMASIADO_GRANDE: 'The message is too long for the board. Shorten it.',
        EPOCA_DISTINTA: 'The contracts board was restored from a backup a moment ago and the office is catching up. Check the conversation and, if still needed, send it again.',
      },
      generico: 'Something failed on the server. Try again in a moment.',
      red: 'No connection with the server. Try again.',
    },
  },
  // ── Calypso Control Room (Pau 30-09-2026) ───────────────────
  // 22-10-2026 — Claude accounts panel (features/oficina/cuentas, docs/cuentas-claude/catalogo-situaciones.md).
  cuentasClaude: {
    titulo: 'Claude accounts',
    placa: 'Claude',
    deCuantas: '{libres} of {leidas}',
    libres: 'free',
    sinLectura: '{n} unread',
    otraEn: 'another {cuando}',
    unaEn: 'one {cuando}',
    sinLecturaHace: 'no reading {hace}',
    abrir: 'Claude accounts: {libres} of {leidas} free',
    cinco: '5 hours',
    semana: 'Week',
    agotada: 'Used up',
    fable: 'Fable {pct} %',
    pagoPorUso: 'Paying per use: {gastado} of {limite}',
    planMenor: '{plan} plan: less capacity',
    leidoHace: 'read {hace}',
    leyendo: 'Reading…',
    sinLecturaDesde: 'No reading for {min} min',
    motivo: {
      espera: 'Anthropic asks to wait. The account works normally.',
      maquina: 'Its machine is not answering. Its agents already work elsewhere.',
      lectura: 'Its usage cannot be read right now.',
    },
    caducada: 'Session expired',
    caducadaNota: 'Its agents already work on other accounts.',
    impagada: 'Payment pending',
    impagadaNota: 'Anthropic could not charge it. It still works, but it will be cancelled: sign in to claude.ai as {nombre} → Settings → Billing and update the card.',
    arreglar: 'Fix it',
    encargo:
      'The Claude account «{nombre}» (runner {runner}) has been failing on the accounts panel for {min} min: {problema}. Find the cause and fix it; if Pau needs to do something, tell him clearly.',
    cerrar: 'Close',
    volver: 'Office',
    reconexion: {
      boton: 'Reconnect',
      titulo: 'Reconnect · {nombre}',
      paso1: 'Sign in with the email of {nombre}',
      paso2: 'Press «Authorize»',
      paso3: 'The office checks it and closes on its own',
      explica: "This window is a browser running on this account's machine, with its own connection. No VPN, Firefox or Cursor needed.",
      noEnTuOrdenador: 'If Claude emails you a link, do not open it on your computer: paste it here.',
      pegarEnlace: 'Paste the email link here',
      abrirEnlace: 'Open here',
      escribir: 'Type here (or click the page and type)',
      enviar: 'Send',
      pantalla: 'Claude sign-in page',
      comprobandoIp: "Checking it goes out through this account's IP…",
      abriendo: 'Opening the Claude sign-in…',
      comprobando: 'Checking the account…',
      conectada: '{nombre} is connected again. Its agents can use it.',
      cancelada: 'Cancelled. Nothing was touched.',
      noSePudo: 'The reconnection could not be opened.',
      sinRespuesta: "This account's machine is not responding. Try again in a while.",
      barra: 'claude.ai',
      reintentar: 'Try again',
    },
    tiempo: {
      enMin: 'in {m} min',
      enH: 'in {h} h',
      enHMin: 'in {h} h {m} min',
      manana: 'tomorrow',
      dias: 'Sun,Mon,Tue,Wed,Thu,Fri,Sat',
      ahora: 'just now',
      haceS: '{s} s ago',
      haceMin: '{m} min ago',
    },
  },
  salaControl: {
    grupos: { negocio: 'Business', calypso: 'Calypso', coste: 'Cost', obras: 'Works' },
    titulo: 'Control Room',
    subtitulo: 'How Calypso is doing, right now · same numbers as the CRM',
    abrir: 'Open the Control Room',
    dias: '{n} days',
    cerrar: 'Close the Control Room',
    alDia: 'up to date · {hora}',
    conRetraso: 'delayed data',
    sinDatos: 'no data yet',
    cargando: 'Loading the numbers…',
    errorCarga: 'Could not load the numbers. Retrying automatically.',
    pestanas: { pulso: 'Pulse', dinero: 'Money', fans: 'Fans', seguridad: 'Safety', maquina: 'Machine', cuentas: 'Accounts & lens', cerebros: 'Brains', gasto: 'Cost', obra: 'Build', taller: 'Workshop' },
    gasto: {
      periodo: 'Period',
      periodos: { hoy: 'Today', ayer: 'Yesterday', '7': '7 D', '30': '30 D', mes: 'This month', fechas: 'Pick dates' },
      desde: 'From',
      hasta: 'to',
      mesesCortos: 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec',
      mesesLargos: 'January February March April May June July August September October November December',
      fecha: '{mes} {dia}',
      rango: '{desde} – {hasta}',
      rangoMismoMes: '{mes} {desde}–{hasta}',
      etiquetaHoy: 'Today, {fecha}',
      etiquetaAyer: 'Yesterday, {fecha}',
      tramo: '{desde}–{hasta} UTC',
      diaCompleto: 'full day',
      diaDesde: 'from {hora} UTC',
      diasConDatos: '{n} of {m} days with data',
      titularAria: 'Profitability of the period',
      frase: 'Serving Calypso takes',
      deNuestraParte: 'of our share',
      barraAria: '{pct} against a target of under 20 %',
      barraObjetivo: 'target < 20 %',
      punto: '1 point',
      puntos: '{n} points',
      porEncima: 'above target',
      porDebajo: 'below target',
      enObjetivo: 'Right on target',
      ayerFue: 'yesterday {pct}',
      anteayerFue: 'the day before {pct}',
      diaAnteriorFue: 'the previous day {pct}',
      mejorDia: 'best day, {fecha}, {pct}',
      conEstimado: '{texto} (estimated)',
      enMes: 'In {mes}: {pct} · {usd}',
      gastado: 'Spent',
      gastadoTramo: 'from {desde} to {hasta} UTC',
      ayerAEstaHora: 'yesterday at this time: {usd}',
      sinApuntesHoy: 'nothing logged for today yet',
      estimadoConConsumo: 'estimated from usage and price lists',
      enParteEstimado: 'partly estimated from usage and price lists',
      registrado: 'logged call by call',
      nuestraParte: 'Our share',
      nuestraParteAyuda: "Our share: 10 % of the net Calypso sells (what is left after OnlyFans' cut; AI PPV and tips, as in the Money tab).",
      deNetos: 'of {usd} net',
      nosQueda: 'We keep',
      nosQuedaDetalle: 'our share minus the cost',
      notaHoyDesde: 'Today is counted from {hora} UTC: nothing was logged before that time.',
      notaCompensado: 'Already taken off the spend: {usd} of {motivo}. What we spend with that credit still counts as spend.',
      compensadoServicio: 'By service is what each one cost; {usd} of {motivo} is already taken off the total.',
      notaFacturaManda: 'OpenRouter{dias} counts what it invoiced ({facturado}); Calypso had logged {apuntado}.',
      notaFacturaMandaCortadas: 'OpenRouter{dias} counts what it invoiced ({facturado}); Calypso had logged {apuntado}. The gap is mostly cut-off replies, which are charged in full.',
      notaFacturaDias: ' (on {n} days of the period)',
      notaEstimadoAntes: 'From 00:00 to {hora} UTC it is estimated (before Calypso logged every call); from then on, logged.',
      notaHorasEstimadas: 'Hatched: hours estimated from usage and price lists (before Calypso logged every call).',
      notaDiaHorasEstimadas: '{dia}: estimated hour by hour from usage and price lists; approximate split and no detail per account or service.',
      cuadreOk: 'Checked against what we were charged: {proveedor} ±{n} %.',
      cuentaN: '{proveedor} (account {n})',
      cuadreMenor: 'What was logged for {proveedor} is {n} % less than what it charged us: that account is shared with other uses.',
      verDetalle: 'See detail',
      nivel: { decision: 'Needs you', aviso: 'Notice' },
      porHora: 'Cost per hour',
      porHoraSub: 'in dollars · UTC',
      porHoraAria: 'Cost per hour, in dollars',
      sinRegistro: 'not logged',
      enCurso: 'in progress',
      sinDatosCorto: 'no data',
      sinDatosTramo: 'no data · {desde} – {hasta}',
      sinApuntesGrafica: 'Nothing has been logged for today yet.',
      sinHoras: 'This day has no hourly detail.',
      porDia: 'How much each day takes',
      porDiaSub: '% of our share',
      porDiaAria: 'Share of our income taken by the cost, per day',
      frenteSemana: '{dia} against the week',
      ayerTitulo: 'Yesterday',
      notaEstimados: '{dias}: estimated from usage and price lists, with no exact split and no detail per hour, account or service.',
      notaDiaEstimado: '{dia} has no hourly detail: estimated from usage and price lists. Hours exist from {inicio}.',
      tipEstimado: 'estimated',
      tipEnCurso: 'in progress: the day is not over',
      tipSinDatos: 'no data',
      enQueSeVa: 'Where it goes',
      repartoAproximado: 'approximate split',
      grupos: {
        conversacion: 'Conversation',
        repeticion: 'Retrying replies that failed',
        revisor: 'Safety reviewer',
        memoria: 'Memory and automatic messages',
        otros: 'Images and other',
      },
      cuentas: 'Accounts losing money',
      cuentasHoy: 'Not calculated while the day is in progress: see Yesterday or 7 days',
      cuentasSinRegistro: 'No per-account detail before {fecha}',
      cuentasNinguna: 'No account cost more than it brings',
      cuentasResumen: '{n} accounts cost more than they bring · −{usd}',
      cuentasResumenUna: '1 account cost more than it brings · −{usd}',
      cuentasPeores: 'The ones losing most',
      cuenta: 'Account',
      nosDeja: 'Brings us',
      nosCuesta: 'Costs us',
      detalle: 'Technical detail',
      detalleSubDia: 'services and what we were charged',
      detalleSubDias: 'day by day and where each figure comes from',
      detalleSubEstimado: 'where each figure comes from',
      porServicio: 'By service',
      llamadas: '{n} calls',
      servicios: {
        deepseek: 'DeepSeek direct',
        deepseekOr: 'DeepSeek via OpenRouter',
        claude: 'Claude (fallback)',
        qwen: 'Qwen (reads images)',
        gemini: 'Gemini',
        otros: 'Other services',
      },
      precio: {
        proveedor: 'invoice price',
        tarifa: 'list price',
        estimado: 'estimated (cut calls)',
        sin_tarifa: 'no price: not counted',
        mixto: 'invoice and list price',
      },
      cuadreTitulo: 'What was logged against what we were charged',
      apuntadoCobrado: '{apuntado} logged · {cobrado} charged',
      sinCobrado: '{apuntado} logged · charge not known yet',
      apuntadoFacturado: '{apuntado} logged · {cobrado} invoiced',
      calculadoFacturado: '{apuntado} computed (partly estimated) · {cobrado} invoiced',
      facturaOtras: 'Other OpenRouter keys (agents and tests): {usd} invoiced separately.',
      compartida: 'That account is shared with other uses.',
      cortadas: 'Calls that were cut',
      cortadasDetalle: '· already included in the cost',
      avisosTecnicos: 'Technical notices',
      notaEstimadosDetalle: 'Estimated days ({dias}) have no call-by-call log: each was estimated from each service\'s usage and price list and the split is approximate. That is why they have no per-service split and no cut calls, and only OpenRouter is checked against its invoice. All of that exists from {inicio} (see Today).',
      diaADia: 'Day by day',
      dia: 'Day',
      seCome: 'Takes',
      comoSeCalculo: 'How it was calculated',
      total: 'Total',
      origen: {
        estimadoCerrado: 'estimated · closed',
        estimadoProvisional: 'estimated · provisional',
        registroCerrado: 'logged · closed',
        registroProvisional: 'logged · provisional',
        sinDatos: 'no data',
        enCurso: 'in progress',
      },
      provisionalNota: '“Provisional”: that day is not closed in the books yet and the figure may move a little.',
      cargandoAria: 'Loading the cost',
      vacioTitulo: 'No cost logged for these dates yet',
      vacioSub: 'Cost is logged from {inicio} (until {fin}, estimated from usage and price lists).',
      verHoy: 'See today',
      calculandoHoyTitulo: 'Today’s cost is being worked out',
      calculandoHoySub: 'The day starts at 00:00 UTC; the first figures appear within a few minutes.',
      notaDetalleMixto: 'From {hora} UTC: by service and by account only count from then (before that it is estimated and has no such detail).',
      error: 'Could not load the cost.',
      reintentar: 'Retry',
      sinRefrescar: 'Could not update · data from {edad} ago',
      actualizado: 'Updated {edad} ago',
      registradoHasta: 'cost logged up to {hora} UTC',
    },
    rincones: {
      cargando: 'Calculating this section…',
      sinDatos: 'No data for this section yet.',
      calculandoPrimera: 'Calculating for the first time: this takes a few seconds…',
      sinDatosPorError: 'Could not read the data: {motivo}.',
      desfasado: 'This data is {edad} old: {motivo}. Retrying automatically.',
      edad: 'Data from {edad} ago',
      errores: {
        sin_credenciales: 'the data store keys are missing',
        tiempo_agotado: 'the data store took too long',
        sin_memoria: 'the query is too big for the data store',
        cuota_agotada: 'the data store quota is used up',
        fuente_cambiada: 'the CRM changed its data and the room needs adapting',
        error: 'could not be updated',
      },
      dinero: {
        ventasHoy: 'Net sales today',
        ppv: 'From PPVs',
        propinas: 'From tips',
        compras: '{n} PPVs bought',
        enChats: 'All spend in Calypso chats',
        enChatsAyuda: 'What fans spend in conversations Calypso runs, even if a human or a mass message sent the PPV',
        porHora: 'Net sales per hour',
        porHoraLeyendaHoy: 'Today',
        porHoraLeyendaAyer: 'Yesterday',
        precios: 'Unlock rate by price',
        preciosSub: 'Last 7 days',
        internas: 'Not included in these figures, as in the CRM: {ventas} from {n} internal accounts (in-house agency).',
      },
      fans: {
        chatean: 'Fans chatting today',
        pagan: 'Fans paying today',
        embudo: 'How does a fan get to buy?',
        embudoSub: 'Last 7 days',
        atendidos: 'Fans Calypso replies to',
        conPpv: 'Get at least one PPV',
        compran: 'Buy at least one',
        repiten: 'Buy again',
        deLosQueReciben: '{p} of those who get one',
        deLosQueCompran: '{p} of buyers',
        semana: 'Day by day · last 7 days',
        semanaSub: 'Closed days; today is hatched because it is still running',
        chateanDia: 'Fans who chat each day',
        paganDia: 'Fans who pay each day',
        minPpv: 'first PPV arrives after {min} min',
        minCompra: 'and the purchase, {min} min later',
      },
      seguridad: {
        escalaciones: 'Why do we hand fans to a human?',
        escalacionesSub: 'Up to this hour',
        clases: { crisis: 'Crisis (fan is not ok)', amenaza_creadora: 'Threat to the creator', menor: 'Minor', otro: 'Other' },
        total: 'Total',
        hoy: 'Today',
        ayer: 'Yesterday',
        sem: 'Last week',
        frenos: 'What was stopped before reaching the fan?',
        frenosSub: 'Today',
        tipos: {
          rewrite_performed: 'Messages rewritten',
          freno_candados: 'Sales held by the locks',
          of_gate_hit: 'OnlyFans forbidden topics',
          safety_block: 'Messages blocked',
          fan_blocklist: 'Fans cut for safety',
          crisis_candado_venta_por_fan: 'Sales stopped by a crisis',
          refusal_detected: 'The AI refused to reply',
        },
        desconocidas: 'There are new escalation types the room does not name yet (counted as «Other»): {lista}.',
      },
      maquina: {
        p95: 'Slowest 5 % take',
        objetivo: 'target 30 s',
        p50: 'Typical reply',
        respaldo: 'Backup model kicked in',
        coste: 'Support tasks cost today',
        costeAyuda: 'Not counting the chat model',
        porHora: 'Who is slow?',
        porHoraSub: 'Slowest 5 % of replies, hour by hour, today',
        tabla: 'By model, last 24 h',
        respuestas: 'Replies',
        tipica: 'Typical',
        lento: 'Slowest 5 %',
        modelos: { 'deepseek-v4-pro': 'Normal model', 'deepseek-v4-pro-ckpt-abril': '«April» arm', 'deepseek-v4-flash': 'Fast model', otro: 'Others' },
      },
      cuentas: {
        total: 'Accounts with Calypso',
        trabajando: 'Working now',
        trabajandoAyuda: 'Replied in the last hour',
        calladas: 'Suddenly silent',
        calladasAyuda: 'Were replying in the previous 3 hours and have not replied for an hour',
        calladasDetalle: 'silent · stopped',
        paradasLista: 'Stopped accounts',
        paradasAyuda: 'Were working this week and have not replied for 24 hours',
        calladasLista: 'Silent accounts',
        experimento: 'Running experiment',
        brazo: 'Arm {b}',
        fans: 'Fans',
        respuestas: 'Replies 24 h',
        buscar: 'Search an account…',
        cuenta: 'Account',
        ventas: 'Sales today',
        unlock: 'Unlock',
        ultima: 'Last reply',
        modelo: 'Model',
        sinResultados: 'No account with that name.',
        masCuentas: 'and {n} more',
      },
    },
    ventasHoy: 'Net sales today',
    ventasHoyAyuda: 'Calypso net sales today · UTC day, from {desde} · up to {hora}',
    vsAyer: 'vs yesterday at this time',
    vsSemana: 'vs the same day last week',
    ayer: 'yesterday',
    semana: 'week',
    semanaCorta: 'wk',
    verTodas: 'Show all {n}',
    verMenos: 'Show fewer',
    igual: '=',
    pts: '{valor} pts',
    acumulado: 'Cumulative net sales today',
    hoy: 'Today',
    ayerLeyenda: 'Yesterday',
    semanaLeyenda: 'Last week',
    grafica: {
      franjaHora: '{desde} – {hasta} UTC',
      delGasto: 'Of the period spend',
      ayerAEstaHora: 'Yesterday by now',
      semanaAEstaHora: 'Last week by now',
      frenteAyer: 'vs yesterday',
      hastaHora: 'Up to {hora} UTC',
      gasto: 'Spend',
      delDia: 'Of the day total',
      seCome: 'Takes',
      gastado: 'Spent',
      nuestraParte: 'Our share',
      estimado: 'Estimated from usage and pricing',
      enCurso: 'The hour being logged right now',
      media7: '7-day average',
      normal: 'Normal range',
      raroAlto: 'Above the normal range',
      raroBajo: 'Below the normal range',
      hoyAMedias: 'Today, so far',
      hoyAMediasPie: 'The day is not over: do not compare it with full days',
      abrirDia: 'Click to open that day',
      anterior: 'Previous period',
      objetivo: 'Target',
      semanaDe: 'Week of {dia}',
      incompleta: 'Week not finished',
      sinDato: 'No data',
    },
    porHora: {
      titulo: 'Hour by hour, today vs yesterday',
      ventas: 'Sales per hour',
      unlock: 'Unlock rate so far today',
      pagan: 'New paying fans',
      reply: 'Reply time so far today',
      cvr: 'Fan conversion so far today',
      porFan: '$ per fan so far today',
      porMsg: '$ per message so far today',
      ultima: 'last closed hour',
      leyendaPie: 'UTC hours · sales and paying fans per hour; the rest, the day up to that hour (like the cards)',
    },
    tabla: {
      titulo: 'Accounts',
      sub: 'Top {n} by sales today · click a column to sort',
      cuenta: 'Account',
      ventas: 'Sales',
      porMsg: '$/message',
      porFan: '$/fan',
      unlock: 'Unlock',
      golden: 'Golden',
      fans: 'Fans',
      cvr: 'Conversion',
      reply: 'Reply',
      estado: 'Status',
      callada: 'quiet',
      parada: 'stopped',
      cargando: 'Loading accounts…',
      sinDatos: 'No accounts with sales today yet.',
    },
    sinAvisos: 'No open alerts',
    diaEntero: { largo: 'yesterday, full day: {valor}', corto: 'yday full {valor}', ayuda: 'The big number is today so far, with the day half done. This is yesterday’s full day.' },
    modelos: { sinComparar: 'no comparison: the previous period is not fully saved yet', vsInicio: '{b} vs {a} (same model)', ventasPeriodo: 'Net sales · {modelo} · {periodo}', vsAnterior: 'vs the same model in {periodo}', anteriorCorto: 'previous', fansPeriodo: '{fans} distinct fans · {pct} % of fans', faltan: 'Days of this period are still being saved ({n} missing): numbers may fall short.', horasDia: 'Hour by hour, this day vs the previous one', ventasDia: 'Net sales per day', familias: { 'deepseek-v4-pro': 'DeepSeek Pro', 'deepseek-v41-flash': 'DeepSeek Flash', 'deepseek-v4-flash': 'DeepSeek Flash' }, titulo: 'View by model', todos: 'All', ventasDe: 'Net sales today · {modelo}', vsAyer: 'vs the same model yesterday at this time', fans: '{fans} fans today · {pct} % of fans', nota: 'Per model, the same CRM numbers split by the model that wrote each conversation (money ~98 %). Reply time cannot be split by model.' },
    parado: { hoy: 'Today', ayer: 'Yesterday', valor: 'Calypso down', nota: 'note: Calypso was down yesterday ({tramos} UTC)', ayuda: 'Calypso barely replied in those hours yesterday: from then on, the comparison with yesterday looks better than it is.' },
    retrasoAviso: 'Some data is delayed: dimmed items are not up to date.',
    pantalla: { ventas: 'Calypso · {hora} UTC', vsAyer: '{delta} vs yesterday' },
    obra: {
      titulo: 'Calypso epic 005',
      sub: 'Read from Calypso code {edad} ago · nothing written by hand',
      cuando: '{dia}, {hora} Thailand time',
      hoy: 'today',
      sinDatos: 'The build status has not been read yet.',
      salud: { verde: 'On track', ambar: 'On track, with a warning', rojo: 'Stalled' },
      saludDetalle: {
        verde: 'Everything started has someone working on it.',
        ambar: '{n} pieces started and nobody is working on them right now.',
        ambarUno: '1 piece started and nobody is working on it right now.',
        rojo: 'Nothing has moved in {dias} days.',
        rojoNunca: 'Nothing has moved yet.',
      },
      cerradas24h: '{n} closed in the last 24 h.',
      cerradas24hUno: '1 closed in the last 24 h.',
      pctEpica: 'of the whole epic',
      piezasDe: '{hechas} of {total} pieces',
      ruta: 'Roadmap',
      rutaSub: 'each stage starts when the previous one has its key pieces ready',
      estamosAqui: 'we are here',
      etapaN: 'Stage {n}',
      etapaCuenta: '{hechas} of {total}',
      avance: 'Day by day · stage {n}',
      avanceSub: 'closed pieces, cumulative',
      meta: 'goal: {n}',
      prevista: 'Expected end of the stage: {fecha}',
      sinPrevista: 'Expected date: shows up with {n} days of data',
      tablero: 'Stage {n} board',
      porHacer: 'To do',
      enCurso: 'In progress',
      hecho: 'Done',
      yMas: 'and {n} more',
      verMenos: 'show less',
      parada: 'stalled · nobody is working on it',
      lleva: 'handled by «{tema}»',
      toca: 'Your turn',
      tocaSub: 'the only thing on your side holding the build',
      allowNada: 'Deploys waiting for your Allow: none',
      allowN: 'Deploys waiting for your Allow: {n}',
      preguntas: 'Unanswered epic questions: {n}',
      preguntasNada: 'No unanswered epic questions',
      frentes: 'Workstreams',
      frentesSub: 'whole epic',
      frentesQuietos: 'and {n} workstreams not started',
      riesgos: 'Risks',
      riesgosSub: 'detected automatically',
      sinRiesgos: 'None right now.',
      riesgo: {
        quieta: 'Nothing has moved in {dias} days.',
        paradas: '{n} pieces started with nobody working on them.',
        paradasUno: '1 piece started with nobody working on it.',
        allow: '{n} deploys are waiting for your Allow.',
        allowUno: '1 deploy is waiting for your Allow.',
        sinEmpezarEtapa: 'Stage {etapa} has not started yet.',
      },
      ultimo: 'Latest',
      hecha: 'Done',
      subida: 'Production deploy',
      errores: {
        sin_github: 'could not fetch the latest from Calypso',
        tablero_roto: 'the Calypso build board failed',
        formato: 'the Calypso build board changed shape',
        error: 'could not read the build',
      },
    },
    enMarcha: {
      titulo: 'Happening now',
      sub: 'deploys and deliveries, live · updates every 10 s',
      nada: 'Nothing is being deployed or delivered right now.',
      ultimaProd: 'Last production deploy: {cuando} ({como}).',
      desde: '{edad} ago',
      empezo: 'started {edad} ago',
      teToca: 'your turn: press Allow',
      recientes: 'Latest deploys (24 h)',
      destino: { produccion: 'Production', pruebas: 'Staging', entrega: 'Delivery' },
      estados: { pendiente: 'pending', en_curso: 'running', esperando_tu: 'waiting for your Allow', hecho: 'done', fallo: 'failed', saltado: 'not needed' },
      resultado: { hecho: 'Done', fallo: 'Failed', cancelado: 'Cancelled', en_curso: 'Running', esperando_tu: 'Waiting for your Allow' },
      fases: {
        prepara: 'Prepares',
        comprueba: 'Automatic checks',
        allow: 'Your Allow',
        sube: 'Goes up to the servers',
        canario: 'Tries it with a few',
        cierra: 'Finishes',
        sube_pruebas: 'Goes up to staging',
      },
      entrega: { comprueba: 'Checks', revisa: 'Another agent reviews it', prepara: 'Prepares to go in', cola: 'In the queue', dentro: 'In the official version' },
    },
    taller: {
      titulo: 'Workshop: agents, lines of work and the official version',
      sub: 'Updated {edad} ago · updates itself every minute',
      actualizarYa: 'Update now',
      actualizando: 'Updating…',
      sinDatos: 'The workshop has not been read yet.',
      salud: { verde: 'Everything flows', ambar: 'Some things to check', rojo: 'Something is stuck' },
      saludVerde: 'Nothing stuck or abandoned.',
      cifraAgentes: 'agents in the office',
      cifraLineas: 'lines of work on GitHub',
      cifraAbandonadas: 'abandoned',
      cifraEntraron: 'changes went in today (24 h)',
      donutTitulo: 'Lines of work on GitHub (branches)',
      donutAria: '{n} lines of work on GitHub, by who has them',
      donutCentro: 'on GitHub',
      lineas: { conAgente: 'From a working agent', conPropuesta: 'Waiting to go in', abandonadas: 'Abandoned (no agent)', vacias: 'Empty (already official)' },
      mirar: 'What needs your attention',
      nadaQueMirar: 'Nothing: everything is on its way.',
      aviso: {
        atascado1: '“{titulo}” is stuck and will not move on its own.',
        atascado: '{n} agents are stuck: they will not move on their own.',
        te_espera1: '“{titulo}” is waiting for your answer.',
        te_espera: '{n} agents are waiting for your answer.',
        quieto1: '“{titulo}” has had nothing to do for over a day: close it or give it work.',
        quieto: '{n} agents have had nothing to do for over a day: close them or give them work.',
        sin_nadie1: '“{titulo}” was left on GitHub without any agent.',
        sin_nadie: '{n} lines of work were left on GitHub without any agent.',
        demasiados: 'Too many abandoned lines on GitHub ({n}). If one is worth it, ask an agent to pick it up; the rest is saved and deleted automatically after 14 days.',
        sin_github: 'GitHub is not answering: agent info is up to date, GitHub info is not.',
      },
      oficial: 'Official version ({nombre})',
      pruebas: { verde: 'checks green', rojo: 'checks red', en_curso: 'checks running', sin_datos: 'no checks' },
      ultimoCambio: 'Last change {edad} ago: {titulo}',
      verEnGitHub: 'See on GitHub',
      verLineas: 'See them on GitHub',
      agentesTitulo: 'Agents in the office ({n})',
      agentesQue: 'Each one works in its own copy on our servers (a so-called worktree). Tap one to open its chat.',
      sinAgentes: 'No agent working in this room right now.',
      yaEnGitHub: 'Its line of work is already on GitHub',
      soloEnSuCopia: 'Only in its copy so far: it reaches GitHub when delivered',
      activo: 'active {edad} ago',
      lineasTitulo: 'Lines of work on GitHub ({n})',
      lineasQue: 'What GitHub calls branches: each is a change not yet in the official version.',
      olvidados: 'Abandoned ({n})',
      flujo: 'How a change gets into the official version',
      flujoSub: 'how many are at each step right now',
      pasoN: 'Step {n}',
      flujoPasos: { haciendo: 'Works in its copy', revisando: 'Another agent reviews it', entrando: 'Waits its turn', entraron: 'Went in (24 h)' },
      flujoQue: {
        haciendo: 'The agent changes things in its own copy; it does not touch the official version yet.',
        revisando: 'Another agent checks it is right before letting it in.',
        entrando: 'Approved: waiting in the queue (pull request) to go into master.',
        entraron: 'Already part of the official version.',
      },
      fuera: 'Stopped agents (out of {n})',
      grupos: { atascado: 'Stuck', te_espera: 'Waiting for you', en_camino: 'On the way', haciendo: 'Working', quieto: 'Paused', sin_nadie: 'Abandoned' },
      gruposQue: {
        atascado: 'Something fails and they will not move on their own.',
        te_espera: 'They need your answer to continue.',
        en_camino: 'In review or waiting to go in.',
        haciendo: 'Working.',
        quieto: 'Finished and waiting for you to tell them something.',
        sin_nadie: 'No agent.',
      },
      pasos: { atascado: 'Stuck', te_espera: 'Waiting for you', revisando: 'In review', entrando: 'Waiting to go in', haciendo: 'Working', quieto: 'Paused', sin_nadie: 'Abandoned' },
      motivos: { no_pasa: 'fails the checks', cierre_atascado: 'got stuck while delivering', fallo_agente: 'its agent has a fault' },
      dejo: 'left by “{tema}”',
      deNadie: 'not opened by any office agent',
      movido: 'touched {edad} ago',
      seRecoge: 'deleted automatically on {fecha}',
      abrirChat: 'Open the chat of “{tema}”',
      verPropuesta: 'See on GitHub',
      ritmo: 'Changes that went into the official version, each day',
      ritmoSub: 'last 14 days · average {media} a day',
      ritmoAria: 'Changes that went into the official version each of the last 14 days, average {media} a day',
      media: 'average',
      olvido: 'Abandoned: how long untouched',
      olvidoSub: '{n} lines of work without an agent',
      tramos: { menos3: 'under 3 days', de3a7: '3 to 7 days', de7a14: '7 to 14 days', mas14: 'over 14 days' },
      olvidoNota: 'After 14 days untouched, the cleanup saves and deletes them on its own: nothing is lost.',
      entraron: 'What went into the official version (last 24 h)',
      entraronNada: 'Nothing in the last 24 hours.',
      yMas: 'and {n} more',
      verMenos: 'see less',
      errores: { error: 'could not read the workshop' },
    },
    cerebros: {
      pantalla: 'Brains',
      titulo: 'What fans get right now',
      sub: '{fans} fans in the last half hour · data from {edad} ago',
      normal: 'The normal',
      normalSub: 'what every fan outside a test gets',
      version: 'Version',
      versionHoy: "today's",
      cerebro: 'Brain answering',
      barra: 'Share of fans by brain',
      cerebroEdad: '{fans} fans answered in the last {min} min · data from {edad} ago',
      deRespaldo: '(backup)',
      respaldo: '{pct} was answered by a backup brain because the assigned one failed.',
      yaParaTodos: 'Now for everyone',
      paraTodos: 'since {fecha} UTC',
      vivas: 'Live tests · {n}',
      sinLados: 'Calypso is not recording which side of each test every fan is on: live tests cannot be known. Check Calypso.',
      ninguna: 'No live tests right now: every fan gets the normal.',
      sinDatos: 'No fans to count yet.',
      barraPrueba: 'Split of the fans of “{nombre}”',
      loNormal: 'the normal',
      soloA: 'Only among {quienes}.',
      llevaHoras: 'version from {n} h ago',
      llevaDia: 'version from 1 day ago',
      llevaDias: 'version from {n} days ago',
      allow: 'Latest version: {fecha} UTC, with your Allow “{titulo}”',
      sinAllow: 'Not in your Allows since {fecha} UTC: switched on earlier or without changing settings',
      sinAllowNunca: 'Unknown which deploy switched it on',
      sinNombre: 'no name in the Calypso registry',
      busca: 'Goal: {texto}',
      numeros: 'Over the last 24 h of its fans (its latest version is older). With few fans differences can be chance: sales are only marked solid when very clear and with at least 1,000 fans per side.',
      numerosVersion: 'Since its latest version ({fecha} UTC, {h} h ago): nothing from before is mixed in. With few fans differences can be chance: sales are only marked solid when very clear and with at least 1,000 fans per side.',
      numerosEn: 'New version: numbers show once it has run for an hour (in {n} min); before that they would say nothing.',
      fila: {
        coste_mil: 'Cost per 1,000 answers',
        cache_pct: 'Cache reused',
        venta_fan: 'What each fan pays',
        bloqueos_mil: 'Blocks per 1,000 answers',
      },
      firme: 'solid difference',
      noFirme: 'could still be chance',
      puntos: '{signo}{n} points',
      nota: 'A test shows here while its fans are split. When they all end up on one side, it disappears on its own.',
    },
    fuentes: {
      crm: 'CRM data: {edad} ago',
      conversaciones: 'Calypso conversations: {edad} ago',
    },
    metricas: {
      unlock: { nombre: 'Unlock rate', ayuda: 'PPVs bought out of every 100 sent' },
      cvr: { nombre: 'Fan conversion', ayuda: 'Fans who pay out of every 100 who chat' },
      golden: { nombre: 'Golden ratio', ayuda: 'Out of every 100 Calypso messages, how many are PPVs' },
      reply: { nombre: 'Reply time', ayuda: 'Average time between the fan message and the Calypso reply' },
      por_fan: { nombre: '$ per fan', ayuda: 'Net sales ÷ fans who chat' },
      por_msg: { nombre: '$ per message', ayuda: 'Net sales ÷ Calypso messages' },
    },
    cache: {
      titulo: 'Model cache · conversations',
      sub: 'Out of every 100 tokens Calypso sends to the model, how many come from the cache (billed much cheaper)',
      vsAnterior: 'vs the previous period ({valor})',
      objetivo: 'Target: {n} %',
      faltan: '{n} points short of the {objetivo} % target',
      porDia: 'Per day · dashed: {n} % target',
      error: 'The cache could not be read. It retries when you come back to the tab.',
      rellenando: '{n} days of these dates or of the compared ones are still being saved: the percentage may change (the full fill takes up to 2 h after start).',
      aparte: 'Pokes and mass messages (another engine): {valor}',
    },
    fechas: {
      atajos: { hoy: 'Today', ayer: 'Yesterday', '7': '7 D', '30': '30 D', mes: 'This month', mes_pasado: 'Last month', '90': '3 M', '180': '6 M', todo: 'All', fechas: 'Dates' },
      atajosLargos: { hoy: 'Today', ayer: 'Yesterday', '7': 'Last 7 days', '30': 'Last 30 days', mes: 'This month', mes_pasado: 'Last month', '90': 'Last 90 days', '180': 'Last 6 months', todo: 'All, since 10 Apr', fechas: 'Dates' },
      anterior: 'Previous period',
      siguiente: 'Next period',
      abrir: 'Choose dates',
      rotuloHoy: 'Today, {fecha}',
      hastaHora: 'until {hora} UTC',
      nDias: '{n} days',
      irA: 'Go to',
      elegirInicio: 'Pick the first day',
      elegirFin: 'Pick the last day',
      aplicar: 'Show',
      cancelar: 'Cancel',
      pie: 'UTC days (they start at 07:00 Thailand time). Data since 10 Apr. Ranges are closed days: today is under «Today».',
      diaRaro: 'Unusual day',
      comparado: {
        hoy: 'Compared with yesterday and last {semana}, at the same time',
        dia: 'Compared with the day before and the same day last week',
        mes_anterior: 'Compared with {mes}, by daily average',
        mes_anteriorIgual: 'Compared with {mes}',
        inicio_final: 'Compared: first month of the range ({a}) vs the last ({b})',
        anterior: 'Compared with the previous {n} days ({a})',
        ninguna: 'No comparison: there is no data before this period',
      },
      sinInternas: 'Could not read which accounts are in-house: these figures may include them and not match the CRM.',
      hoyFuera: 'Today is not included: it is in progress. See it under «Today».',
      cargando: 'Calculating the period…',
      error: 'The period could not be calculated. It retries when you pick it again.',
      rellenando: '{n} days of this period are still being saved: figures may change in a few minutes.',
    },
    esperado: {
      leyenda: 'Expected',
      titulo: 'Expected today at this pace',
      horquilla: 'between {bajo} and {alto}',
      horquillaAyuda: 'Expected earnings today at this pace: between {bajo} and {alto}, based on how recent days ended',
    },
    lectura: {
      tituloRango: 'The period at a glance',
      cambioItem: '{nombre} {de} → {a}',
      ventas: {
        dia: 'Calypso sold {total} that day{cambio}.',
        rango: 'Calypso sold {total}: {media} per day on average{cambio}.',
        cambioAnterior: ', {pct} than the previous period',
        cambioMes: ', {pct} per day than the previous month',
        cambioInicioFinal: '; the last month, {pct} per day than the first',
        cambioDia: ', {pct} than the day before',
        mas: '{n} % more',
        menos: '{n} % less',
        igual: 'about the same',
      },
      fans: '{distintos} different fans chatted ({media} per day on average){cambio}.',
      fansDia: '{fans} fans chatted{cambio}.',
      fansDiaCambio: ', {pct} than the day before',
      fansCambio: '; {pct} than the compared period ({a} → {b})',
      volumen: 'We grow by volume, not efficiency: $ per fan from {a} to {b}.',
      mejoran: 'Improving: {lista}.',
      empeoran: 'Getting worse: {lista}.',
      momentos: '{n} unusual moments; the strongest, {cuando}: {claves}.',
      momento: '1 unusual moment, {cuando}: {claves}.',
      sinMomentos: 'No day was outside normal.',
      faltan: '{n} days are still being saved: the summary may change.',
    },
    series: { ventas: 'Net sales', fans: 'Fans who chat', msgs: 'Calypso messages', unlock: 'Unlock rate', cvr: 'Fan conversion', golden: 'Golden ratio', reply: 'Reply time', por_msg: '$ per message' },
    rango: {
      ventasDe: 'Net sales · {periodo}',
      mediaDia: 'On average, {media} per day.',
      inicioFinal: 'First month: {a} per day · last month: {b} per day',
      vsComparado: '{cambio} per day than {periodo} ({valor} $/day)',
      porDiaNota: 'The periods do not have the same number of days: the daily average is compared.',
      tarjetaInicioFinal: '1st month → last',
      tarjetaAnterior: 'before',
      porFanDistinto: 'per different fan in the period',
      porSemana: 'per week',
      ventasPorDia: 'Net sales per day',
      ventasPorDiaSub: 'Bars: each day · line: 7-day average · grey: normal (4 weeks before, without unusual days) · red: unusual day',
      lineaComparado: 'Dashed: {periodo}',
      momentos: 'Unusual moments in the period',
      momentosSub: 'Detected automatically: a figure leaves its normal band. Click one to see that day.',
      cambioNivel: 'level change',
      paraBien: 'for the better',
      verMas: 'Show {n} more',
      verMenos: 'Show less',
      sinMomentos: 'No day was outside normal in this period.',
      fansTitulo: 'Fans who chat with Calypso',
      fansDia: 'Fans who chat each day',
      fansDiaSub: 'A fan counts once per day · red: unusual day',
      distintos: 'Different fans, no repeats',
      distintosSub: 'If a fan writes on 10 days, they count once here',
      enElPeriodo: 'In the period',
      sumaDias: 'adding up the days would give {n}',
      alDia: 'Per day, on average',
      porSemanaTitulo: 'Per week (Monday to Sunday)',
      rayadas: 'Striped: incomplete weeks, do not compare them with the others.',
      porMes: 'Per month',
      porMesSub: 'Months have different lengths: compare the daily average and $ per fan, not the totals',
      mes: 'Month',
      incompleto: '(incomplete)',
      ventasDia: 'Sales per day',
      porFanCol: '$ per different fan',
      conversionCol: 'Conversion',
      vueltas: 'Each fan chats on average {n} days out of every {d}.',
      cuentas: 'Accounts',
      cuentasSub: 'The 100 that sold the most · click a column to sort',
      alDiaCol: 'Per day',
      progreso: 'Progress',
      progresoAyuda: 'Sales per day of the judged period vs the reference period',
      tendencia: 'Trend',
      nueva: 'new since {dia}',
      unDia: 'One day',
      horasDia: 'Cumulative net sales, hour by hour',
      sinHoras: 'This day does not have its hours saved yet.',
      loNormal: 'normal, {bajo} – {alto}',
      vsDiaAnterior: 'vs the day before',
      verDia: 'Open that day',
    },
    hoyExtra: {
      normal: 'normal at this time',
      normalCorto: 'normal',
      dentro: 'inside',
      fuera: 'outside',
      fansHoy: 'Fans who chatted today',
      fansHoySub: 'Each fan counts once a day, even if they write 50 messages',
      hastaAhora: 'Today, until {hora}',
      ayerAEstaHora: 'yesterday at this time: {n}',
      semanaAEstaHora: 'last {dia} at this time: {n}',
      ultimos30: 'Fans who chat each day · last 30 closed days',
      distintos7: 'Last 7 days',
      distintos30: 'Last 30 days',
      sumaBarras: 'The bars of 7 days add up to {suma}: each fan chats on average {n} days a week. That is why different fans are never the sum of the days.',
    },
  },
} as const;

// Widen literal string types to `string` so es.ts can hold different
// values while enforcing identical key structure.
type DeepStringify<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : DeepStringify<T[K]>;
};

export type Translations = DeepStringify<typeof en>;
export default en;
